// Removed stray code-fence line
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { TextSelection } from '@tiptap/pm/state';
import { TextColorSmallIcon } from '../../../@/components/tiptap-icons/text-color-small-icon';
import { ListIcon } from '../../../@/components/tiptap-icons/list-icon';
import { ListOrderedIcon } from '../../../@/components/tiptap-icons/list-ordered-icon';
import { BlockquoteIcon } from '../../../@/components/tiptap-icons/blockquote-icon';
import { uploadToAzure } from '../direct_uploads/tiptap_direct_upload.js';
import { ResourceModal } from './ResourceModal.jsx';
import { QuoteColorPickerMenu } from './QuoteColorPickerMenu.jsx';
import { TextColorListMenu } from './TextColorListMenu.jsx';
import { parseEmbedProvider } from '../extensions/EmbedVideoExtension.js';
import { normalizePsTextColorToken } from '../extensions/PsTextColorExtension.js';
import {
  POST_EDITOR_FONT_FAMILIES,
  POST_EDITOR_PARAGRAPH_STYLE_OPTIONS,
  POST_EDITOR_FONT_CLASS_VALUES,
  POST_EDITOR_INLINE_TEXT_COLORS,
  postEditorToolbarTextColorSwatches,
  POST_EDITOR_TOOLBAR_BG_CLASS_BY_HEX,
  POST_EDITOR_TOOLBAR_TEXT_CLASS_BY_HEX,
  POST_EDITOR_SPACER_SIZE_KEYS,
  POST_EDITOR_SPACER_SIZE_LABELS,
  POST_EDITOR_SPACER_SIZES,
  POST_EDITOR_SPACER_DEFAULT_SIZE,
  normalizePostSpacerSize,
} from '../lib/post_editor_constants.js';
import { insertPostImageCarousel } from '../extensions/PostImageCarousel.js';
import { mergeBlockIndentAttrs } from '../lib/post_block_indent.js';
import { clipboardPlainLooksLikeHtml } from '../lib/post_editor_paste.js';
import {
  carouselSlideFromResource,
  propagateCarouselSlideMediaToSharedResourceIds,
} from '../lib/carousel_slide_from_resource.js';
import {
  paragraphRowRole,
  RESOURCE_ROW_TITLE_PARAGRAPH_ATTRS,
  afterResourceRowInsertFocus,
  resolveResourceTableRowInsert,
} from '../lib/resource_row_paragraph.js';
import {
  POST_EDITOR_HR_COLORS,
  POST_EDITOR_HR_WIDTHS,
  postEditorHrColorLabel,
  POST_EDITOR_ORDERED_MARKER_COLORS,
  POST_EDITOR_QUOTE_SWATCHES,
  POST_EDITOR_COLOR_PICKER_MODES,
  POST_EDITOR_ANNOUNCEMENT_TEXT_COLORS,
  normalizeOrderedMarkerColor,
  postEditorHrClassList,
  isPostEditorMediaCaptionContext,
  resolvePostEditorColorPickerContext,
  isPostEditorParagraphStyleMenuLocked,
  postEditorParagraphStyleLockTooltip,
  POST_EDITOR_BLOCKQUOTE_LIST_LOCK_TOOLTIP,
  POST_EDITOR_BLOCKQUOTE_HR_LOCK_TOOLTIP,
  POST_EDITOR_HEADING_LIST_LOCK_TOOLTIP,
  postEditorInlineLinkLockTooltip,
} from '../lib/post_editor_constants.js';
import {
  POST_EDITOR_HEADING_ACCENTS,
  effectivePostParagraphStyle,
  isPostEditorInlineLinkLocked,
  isPostEditorListLockedForParagraphStyle,
  paragraphStyleLocksFontFamily,
  selectionInRestrictedInlineLinkContext,
} from '../lib/post_editor_paragraph_style.js';
import { useTiptapActiveEditor } from './TiptapActiveEditorContext.jsx';

/** ProseMirror posAtDOM may return -1 when the DOM node is not mapped; never pass that to doc.nodeAt. */
function isValidPosAtDOM(editor, pos) {
  if (!editor?.state?.doc || typeof pos !== 'number' || Number.isNaN(pos)) return false;
  const max = editor.state.doc.content.size;
  return pos >= 0 && pos <= max;
}

function getSelectionFontClassFromDom(editor) {
  if (!editor?.view || !editor?.state) return null;

  const { from } = editor.state.selection;
  let domNode = editor.view.nodeDOM(from);

  if (!domNode) {
    const domAtPos = editor.view.domAtPos(from);
    domNode = domAtPos?.node || null;
  }

  if (domNode?.nodeType === Node.TEXT_NODE) {
    domNode = domNode.parentElement;
  }

  if (!(domNode instanceof Element)) return null;

  const classHost = domNode.closest('[class*="font-"]');
  if (!(classHost instanceof Element) || !classHost.classList) return null;

  for (const cls of classHost.classList) {
    if (POST_EDITOR_FONT_CLASS_VALUES.has(cls)) return cls;
  }

  return null;
}

function toolbarClassToken(token) {
  const normalized = normalizePsTextColorToken(token);
  if (!normalized) return '';
  return normalized.replace(/^ps-(black|white)$/, '$1');
}

function pickerFgClassFromHex(hex) {
  const h = (hex || '').toString().toLowerCase();
  for (const sw of postEditorToolbarTextColorSwatches()) {
    if (sw.hex.toLowerCase() === h && sw.className) {
      return sw.className.replace('ps-swatch-bg--', 'ps-swatch-fg--');
    }
  }
  return '';
}

function pickerBgClassFromHex(hex) {
  const h = (hex || '').toString().toLowerCase();
  for (const sw of postEditorToolbarTextColorSwatches()) {
    if (sw.hex.toLowerCase() === h && sw.className) return sw.className;
  }
  return '';
}

function toolbarSwatchClasses(hex) {
  const token = hex?.toString().match(/^var\(--([a-z0-9-]+)\)$/i)?.[1] || '';
  if (token) {
    const classToken = toolbarClassToken(token);
    return {
      bg: classToken ? `ps-swatch-bg--${classToken}` : '',
      text: classToken ? `ps-swatch-fg--${classToken}` : '',
    };
  }

  const h = (hex || '').toString().toUpperCase();
  return {
    bg: POST_EDITOR_TOOLBAR_BG_CLASS_BY_HEX[h] || pickerBgClassFromHex(hex) || '',
    text: POST_EDITOR_TOOLBAR_TEXT_CLASS_BY_HEX[h] || pickerFgClassFromHex(hex) || '',
  };
}

function orderedMarkerColorLabel(hex) {
  const h = (hex || '').toString().toUpperCase();
  switch (h) {
    case '#000000':
      return 'Black';
    case '#9BA854':
      return 'Moss';
    case '#A8483D':
      return 'Brick';
    case '#F03E3E':
      return 'Red';
    case '#61584F':
      return 'Gray 07';
    case '#522A45':
      return 'Plum';
    default:
      return h || 'Color';
  }
}

function colorTriggerIconClass(state, ctx) {
  switch (ctx.mode) {
    case POST_EDITOR_COLOR_PICKER_MODES.heading:
    case POST_EDITOR_COLOR_PICKER_MODES.subheading:
      return `ps-heading-${state.postTextAccent || 'black'}`;
    case POST_EDITOR_COLOR_PICKER_MODES.quote: {
      const sw = POST_EDITOR_QUOTE_SWATCHES.find((s) => s.id === (state.quoteVariant || 'black'));
      return toolbarSwatchClasses(sw?.hex).text || undefined;
    }
    case POST_EDITOR_COLOR_PICKER_MODES.orderedList:
      return toolbarSwatchClasses(state.orderedListMarker).text || undefined;
    case POST_EDITOR_COLOR_PICKER_MODES.announcement:
    case POST_EDITOR_COLOR_PICKER_MODES.full: {
      const token = normalizePsTextColorToken(state.color) || 'ps-black';
      const swatch = postEditorToolbarTextColorSwatches().find(
        (s) => normalizePsTextColorToken(s.id) === token
      );
      return toolbarSwatchClasses(swatch?.hex || '#000000').text || undefined;
    }
    default:
      return toolbarSwatchClasses(`var(--${state.color || 'ps-black'})`).text || undefined;
  }
}

export const TiptapToolbar = ({
  editor: mainEditorFromProps,
  onFileUpload = () => {},
  isLoading,
  className = '',
  enableUrlPasteHandler = false,
  toolbarMode = 'full',
  featuresPreset = 'posts',
  features = {}
}) => {
  const activeOverlay = useTiptapActiveEditor();
  const editor = activeOverlay?.activeOverlayEditor ?? mainEditorFromProps;
  const enableResourceUI = features.resources !== false;
  const enableMediaUI = features.media !== false;
  const enableFileUI = features.files !== false;
  const enableSuperscript = features.superscript === true;
  const enableVideoEmbedOnPaste = features.pasteVideoEmbeds === true;
  const enablePasteResourceHandler = typeof features.pasteResourceHandler === 'boolean'
    ? features.pasteResourceHandler
    : enableUrlPasteHandler;

  // Listen for custom event to insert external link row (from paste handler)
  useEffect(() => {
    const handler = (event) => {
      if (!editor) return;
      if (!enableResourceUI) return;
      const url = event?.detail?.url;
      if (!url) return;
      // Use the same logic as insertExternalLink, but skip prompt
      const insertCtx = resolveResourceTableRowInsert(editor.state);
      if (insertCtx) {
        const { insertPos } = insertCtx;
        editor.chain().focus().setTextSelection(insertPos).insertContent({
          type: 'externalLinkRow',
          attrs: {
            href: url,
          },
          content: [
            {
              type: 'paragraph',
              content: [
                {
                  type: 'text',
                  text: 'Click to edit title'
                }
              ]
            }
          ]
        }).run();
        afterResourceRowInsertFocus(editor, editor.state, {
          insertPos,
          tablePos: insertCtx?.tablePos,
          rowType: 'externalLinkRow',
        });
      } else {
        const { $from } = editor.state.selection;
        const before = $from.nodeBefore;
        const after = $from.nodeAfter;
        if (before && before.type.name === 'resourceTable') {
          const beforePos = $from.pos - before.nodeSize;
          const beforeTableEndPos = beforePos + before.nodeSize - 1;
          editor.chain().focus().setTextSelection(beforeTableEndPos).insertContent({
            type: 'externalLinkRow',
            attrs: {
              href: url,
            },
            content: [
              {
                type: 'paragraph',
                content: [
                  {
                    type: 'text',
                    text: 'Click to edit title'
                  }
                ]
              }
            ]
          }).run();
          afterResourceRowInsertFocus(editor, editor.state, {
            insertPos: beforeTableEndPos,
            tablePos: beforePos,
            rowType: 'externalLinkRow',
          });
        } else if (after && after.type.name === 'resourceTable') {
          const afterPos = $from.pos;
          const afterTableEndPos = afterPos + after.nodeSize - 1;
          editor.chain().focus().setTextSelection(afterTableEndPos).insertContent({
            type: 'externalLinkRow',
            attrs: {
              href: url,
            },
            content: [
              {
                type: 'paragraph',
                content: [
                  {
                    type: 'text',
                    text: 'Click to edit title'
                  }
                ]
              }
            ]
          }).run();
          afterResourceRowInsertFocus(editor, editor.state, {
            insertPos: afterTableEndPos,
            tablePos: afterPos,
            rowType: 'externalLinkRow',
          });
        } else {
          const beforePos = editor.state.selection.from;
          editor.chain().focus().setExternalLink({
            href: url,
          }).run();
          afterResourceRowInsertFocus(editor, editor.state, {
            rowType: 'externalLinkRow',
            insertPos: beforePos,
          });
        }
      }
    };
    window.addEventListener('tiptap-insert-external-link', handler);
    return () => window.removeEventListener('tiptap-insert-external-link', handler);
  }, [editor, enableResourceUI]);
  
  const [toolbarState, setToolbarState] = useState({});
  const [isUploadingDownload, setIsUploadingDownload] = useState(false);
  const [showColorDropdown, setShowColorDropdown] = useState(false);
  const [resourceModalOpen, setResourceModalOpen] = useState(false);
  const [resourceModalType, setResourceModalType] = useState(null);
  const [initialLinkUrl, setInitialLinkUrl] = useState('');
  const [initialFiles, setInitialFiles] = useState(null);
  const fileInputRef = useRef(null);
  const downloadInputRef = useRef(null);
  const colorDropdownRef = useRef(null);
  const currentDownloadInsertPos = useRef(null);
  const currentDownloadInsertPlan = useRef(null);
  const audioInputRef = useRef(null);
  const [isUploadingAudio, setIsUploadingAudio] = useState(false);
  const currentAudioInsertPlan = useRef(null);
  const [hrPickerOpen, setHrPickerOpen] = useState(false);
  const hrPickerRef = useRef(null);
  const [spacerPickerOpen, setSpacerPickerOpen] = useState(false);
  const spacerPickerRef = useRef(null);
  const handleCarouselInsert = useCallback((slides) => {
    if (!editor || !slides?.length) return;
    const node = insertPostImageCarousel(slides, editor.state);
    if (!node) return;
    editor.chain().focus().insertContent(node).run();
  }, [editor]);

  // Replace the slides of an existing carousel (edit-carousel mode). Looks up
  // the carousel via `currentCarouselEditPos`, verifies the node is still a
  // postImageCarousel (the doc may have shifted between open/close), and
  // mutates the node's `slides` attribute via setNodeMarkup. We fall back to
  // a fresh insert if the captured position is no longer valid so the user's
  // selection isn't silently lost.
  const handleCarouselUpdate = useCallback(
    (slides) => {
      if (!editor || !slides?.length) return;
      const pos = currentCarouselEditPos.current;
      if (typeof pos !== 'number' || pos < 0) {
        handleCarouselInsert(slides);
        return;
      }
      const nodeAtPos = editor.state.doc.nodeAt(pos);
      if (!nodeAtPos || nodeAtPos.type.name !== 'postImageCarousel') {
        handleCarouselInsert(slides);
        return;
      }
      editor
        .chain()
        .focus()
        .command(({ tr, dispatch }) => {
          if (!dispatch) return false;
          tr.setNodeMarkup(pos, undefined, { ...nodeAtPos.attrs, slides });
          return true;
        })
        .run();
    },
    [editor, handleCarouselInsert]
  );

  useEffect(() => {
    if (!hrPickerOpen) return;
    const close = (e) => {
      if (hrPickerRef.current && !hrPickerRef.current.contains(e.target)) {
        setHrPickerOpen(false);
      }
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [hrPickerOpen]);

  useEffect(() => {
    if (!spacerPickerOpen) return;
    const close = (e) => {
      if (spacerPickerRef.current && !spacerPickerRef.current.contains(e.target)) {
        setSpacerPickerOpen(false);
      }
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [spacerPickerOpen]);

  // Auto-open the spacer size picker when an existing spacer becomes the
  // current node-selection (e.g. the editor clicked on an invisible spacer).
  // Auto-close it again when selection moves off the spacer.
  const prevSpacerSelectedRef = useRef(false);
  useEffect(() => {
    const wasSelected = prevSpacerSelectedRef.current;
    const isSelected = !!toolbarState.spacer;
    if (isSelected && !wasSelected) {
      setSpacerPickerOpen(true);
    } else if (!isSelected && wasSelected) {
      setSpacerPickerOpen(false);
    }
    prevSpacerSelectedRef.current = isSelected;
  }, [toolbarState.spacer]);

  // Update toolbar state when editor changes
  useEffect(() => {
    if (!editor) return;

    const updateState = () => {
      const imageAttrs = editor.getAttributes('image');
      // Pathstone text colors are class-based via the `psTextColor` mark.
      // Avoid `textStyle` — it serializes inline styles. Typography is class-based:
      // - `fontFamilyClass` mark for font changes
      // - `psTextColor` mark for text color
      const psTextColorAttrs = editor.getAttributes('psTextColor');
      const fontFamilyClassAttrs = editor.getAttributes('fontFamilyClass');
      const paragraphAttrs = editor.getAttributes('paragraph');
      
      // Map old font family values to new class names for backward compatibility
      const fontFamilyMap = {
        '"Calibre", sans-serif': 'font-primary',
        '"Calibre Black", sans-serif': 'font-primary-black',
        '"Calibre Bold", sans-serif': 'font-primary-bold',
        '"Calibre Medium", sans-serif': 'font-primary-medium',
        '"Filosofia OT", serif': 'font-secondary',
        '"Filosofia OT Italic", serif; font-style: italic;': 'font-secondary-italic',
        '"Filosofia OT Italic", serif': 'font-secondary-italic',
      };
      let fontFamilyClass =
        fontFamilyClassAttrs.fontFamilyClass ||
        getSelectionFontClassFromDom(editor) ||
        'font-primary';

      // Legacy inline font-family (from old saves) is intentionally not pulled
      // into toolbar state — the goal is to stop emitting persisted inline styles.
      // Check for inline style on the selected node (legacy content)
      // Only check superscript when extension is loaded (posts only)
      const state = {
        underline: editor.isActive('underline'),
        strike: editor.isActive('strike'),
        subscript: editor.isActive('subscript'),
        superscript: editor.schema.marks.superscript ? editor.isActive('superscript') : false,
        link: editor.isActive('link') || (imageAttrs.src && imageAttrs.href),
        bulletList: editor.isActive('bulletList'),
        orderedList: editor.isActive('orderedList'),
        orderedListMarker: editor.isActive('orderedList')
          ? normalizeOrderedMarkerColor(editor.getAttributes('orderedList').markerColor)
          : '#000000',
        blockquote: editor.isActive('blockquote'),
        quoteVariant: editor.isActive('blockquote')
          ? editor.getAttributes('blockquote').variant
          : null,
        // True when the cursor sits anywhere inside a resourceTable
        // (download / external link / meeting / audio rows). Each row's
        // visual styling is fixed by its node spec, so we use this to
        // disable text-color changes while in any of them.
        resourceTable: editor.schema.nodes.resourceTable
          ? editor.isActive('resourceTable')
          : false,
        mediaCaption: isPostEditorMediaCaptionContext(editor, activeOverlay),
        // True when a postSpacer node is the current NodeSelection — used
        // to highlight the toolbar button and pre-fill the picker with the
        // spacer's existing size for in-place resizing.
        spacer: editor.schema.nodes.postSpacer
          ? editor.isActive('postSpacer')
          : false,
        spacerSize: editor.schema.nodes.postSpacer && editor.isActive('postSpacer')
          ? normalizePostSpacerSize(editor.getAttributes('postSpacer').size)
          : null,
        color: normalizePsTextColorToken(psTextColorAttrs.token) || 'ps-black',
        fontFamilyClass,
        postTextStyle: paragraphAttrs.postTextStyle || null,
        postTextAccent: paragraphAttrs.postTextAccent || null,
      };
      setToolbarState(state);
    };

    // Listen for editor updates
    editor.on('selectionUpdate', updateState);
    editor.on('transaction', updateState);
    updateState();

    return () => {
      editor.off('selectionUpdate', updateState);
      editor.off('transaction', updateState);
    };
  }, [editor, activeOverlay]);

  // Track the position of the row being replaced
  const currentReplaceRowPos = useRef(null);
  const currentReplaceRowType = useRef(null);

  // Track carousel edit / per-slide replace context. When the modal is open
  // in either of these modes, ResourceModal pre-selection / single-pick
  // handlers route through these refs instead of the regular insert flow.
  // - currentCarouselEditPos: nodePos of the carousel being edited (Edit mode)
  // - currentCarouselEditSlides: snapshot of the carousel's current slides,
  //   passed to ResourceModal so it can pre-select the matching resources
  // - currentCarouselReplaceSlideIdx: when replacing one slide of a carousel
  //   via the per-slide bubble menu, this tracks which index to swap.
  const currentCarouselEditPos = useRef(null);
  const [currentCarouselEditSlides, setCurrentCarouselEditSlides] = useState(null);
  const currentCarouselReplaceSlideIdx = useRef(null);
  const currentCarouselReplaceSlide = useRef(null);
  const currentCarouselReplaceLockedSlides = useRef([]);

  // Listen for download-row double-click replacement requests coming from
  // the rendered DOM of download rows. The extension will dispatch a
  // 'tiptap:downloadRow:requestReplace' CustomEvent from the <tr> element.
  useEffect(() => {
    const handler = (event) => {
      if (!editor) return;
      if (!enableResourceUI) return;
      // event.target should be the <tr[data-resource-row]>
      const tr = event.target;
      if (!(tr && tr.closest && tr.getAttribute && tr.getAttribute('data-resource-row') !== null)) return;
      try {
        const pos = editor.view.posAtDOM(tr);

        if (isValidPosAtDOM(editor, pos)) {
          // Find the downloadRow node by traversing up from the current position
          let downloadRowPos = null;
          let downloadRowNode = null;
          
          // Check if the node at pos is already a downloadRow
          let currentNode = editor.state.doc.nodeAt(pos);
          let currentPos = pos;
          
          // If we got a paragraph or other inner node, traverse up to find the downloadRow
          if (currentNode && currentNode.type.name !== 'downloadRow') {
            // Walk backwards through positions to find the downloadRow container
            for (let checkPos = pos; checkPos >= 0; checkPos--) {
              const nodeAtCheckPos = editor.state.doc.nodeAt(checkPos);
              if (nodeAtCheckPos && nodeAtCheckPos.type.name === 'downloadRow') {
                downloadRowPos = checkPos;
                downloadRowNode = nodeAtCheckPos;
                break;
              }
            }
          } else if (currentNode && currentNode.type.name === 'downloadRow') {
            downloadRowPos = currentPos;
            downloadRowNode = currentNode;
          }
          
          if (downloadRowNode && downloadRowPos !== null) {
            // Store the position for replacement, but don't delete the node yet
            // Only replace when a file is actually selected in the modal
            currentReplaceRowPos.current = downloadRowPos;
            currentReplaceRowType.current = 'download';
            setResourceModalType('download');
            setResourceModalOpen(true);
          } else {
            console.warn('Could not find downloadRow node for replacement');
          }
        }
      } catch (err) {
        console.error('Failed to handle downloadRow replacement request', err);
      }
    };

    document.addEventListener('tiptap:downloadRow:requestReplace', handler);
    return () => document.removeEventListener('tiptap:downloadRow:requestReplace', handler);
  }, [editor, enableResourceUI]);

  // Listen for audio-row double-click replacement requests
  useEffect(() => {
    const handler = (event) => {
      if (!editor) return;
      if (!enableResourceUI) return;
      let tr = event.detail?.trElement || event.target;
      if (!tr) return;
      if (tr.nodeType !== Node.ELEMENT_NODE || tr.tagName !== 'TR' || !tr.hasAttribute('data-resource-row')) {
        tr = tr.closest && tr.closest('tr[data-resource-row]');
      }
      if (!tr || !tr.getAttribute || tr.getAttribute('data-resource-row') === null) return;
      const rowType = tr.getAttribute('data-row-type');
      if (rowType !== 'audio') return;
      try {
        const pos = editor.view.posAtDOM(tr);
        if (!isValidPosAtDOM(editor, pos)) return;
        let audioRowPos = null;
        let audioRowNode = null;
        let currentNode = editor.state.doc.nodeAt(pos);
        if (currentNode && currentNode.type.name !== 'audioRow') {
          for (let checkPos = pos; checkPos >= 0; checkPos--) {
            const nodeAtCheckPos = editor.state.doc.nodeAt(checkPos);
            if (nodeAtCheckPos && nodeAtCheckPos.type.name === 'audioRow') {
              audioRowPos = checkPos;
              audioRowNode = nodeAtCheckPos;
              break;
            }
          }
        } else if (currentNode && currentNode.type.name === 'audioRow') {
          audioRowPos = pos;
          audioRowNode = currentNode;
        }
        if (audioRowNode && audioRowPos !== null) {
          currentReplaceRowPos.current = audioRowPos;
          currentReplaceRowType.current = 'audio';
          setResourceModalType('audio');
          setResourceModalOpen(true);
        }
      } catch (err) {
        console.error('Failed to handle audioRow replacement request', err);
      }
    };
    document.addEventListener('tiptap:audioRow:requestReplace', handler);
    return () => document.removeEventListener('tiptap:audioRow:requestReplace', handler);
  }, [editor, enableResourceUI]);

  // Handle clicking outside color dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (colorDropdownRef.current && !colorDropdownRef.current.contains(event.target)) {
        setShowColorDropdown(false);
      }
    };

    if (showColorDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showColorDropdown]);

  // Listen for external-link-row double-click replacement requests
  useEffect(() => {
    const handler = (event) => {
      if (!editor) return;
      if (!enableResourceUI) return;
      // event.target should be the <tr[data-resource-row]>
      // For events dispatched from drag context menu, event.target might be the tr element
      // For events dispatched from double-click, event.target might be a child element
      // For events dispatched from floating menu, check detail.trElement first
      let tr = event.detail?.trElement || event.target;
      if (!tr) {
        console.warn('[TiptapToolbar] event.target and detail.trElement are null');
        return;
      }
      
      // If event.target is not the tr element, try to find it
      if (tr.nodeType !== Node.ELEMENT_NODE || tr.tagName !== 'TR' || !tr.hasAttribute('data-resource-row')) {
        tr = tr.closest && tr.closest('tr[data-resource-row]');
      }
      
      if (!tr || !tr.getAttribute || tr.getAttribute('data-resource-row') === null) {
        console.warn('[TiptapToolbar] Could not find tr[data-resource-row] element in externalLinkRow replacement handler', event.target, event.detail);
        return;
      }
      
      try {
        const pos = editor.view.posAtDOM(tr);

        if (isValidPosAtDOM(editor, pos)) {
          // Find the externalLinkRow node by traversing up from the current position
          let externalLinkRowPos = null;
          let externalLinkRowNode = null;
          
          // Check if the node at pos is already an externalLinkRow
          let currentNode = editor.state.doc.nodeAt(pos);
          let currentPos = pos;
          
          // If we got a paragraph or other inner node, traverse up to find the externalLinkRow
          if (currentNode && currentNode.type.name !== 'externalLinkRow') {
            // Walk backwards through positions to find the externalLinkRow container
            for (let checkPos = pos; checkPos >= 0; checkPos--) {
              const nodeAtCheckPos = editor.state.doc.nodeAt(checkPos);
              if (nodeAtCheckPos && nodeAtCheckPos.type.name === 'externalLinkRow') {
                externalLinkRowPos = checkPos;
                externalLinkRowNode = nodeAtCheckPos;
                break;
              }
            }
          } else if (currentNode && currentNode.type.name === 'externalLinkRow') {
            externalLinkRowPos = currentPos;
            externalLinkRowNode = currentNode;
          }
          
          if (externalLinkRowNode && externalLinkRowPos !== null) {
            // Store the position for replacement, but don't delete the node yet
            // Only replace when a resource is actually selected in the modal
            currentReplaceRowPos.current = externalLinkRowPos;
            currentReplaceRowType.current = 'link';
            setResourceModalType('link');
            setResourceModalOpen(true);
          } else {
            console.warn('Could not find externalLinkRow node for replacement');
          }
        }
      } catch (err) {
        console.error('Failed to handle externalLinkRow replacement request', err);
      }
    };

    document.addEventListener('tiptap:externalLinkRow:requestReplace', handler);
    return () => document.removeEventListener('tiptap:externalLinkRow:requestReplace', handler);
  }, [editor, enableResourceUI]);

  // Listen for image replacement requests from drag context menu
  useEffect(() => {
    const handler = (event) => {
      if (!editor) return;
      if (!enableMediaUI) return;
      
      try {
        const nodePos = event.detail?.nodePos;
        if (typeof nodePos !== 'number' || nodePos < 0) {
          console.warn('[TiptapToolbar] Invalid nodePos in image replacement request:', nodePos);
          return;
        }
        
        const nodeAtPos = editor.state.doc.nodeAt(nodePos);
        if (!nodeAtPos || nodeAtPos.type.name !== 'image') {
          console.warn('[TiptapToolbar] Node at position is not an image:', nodeAtPos?.type?.name);
          return;
        }
        
        // Store the position for replacement
        currentReplaceRowPos.current = nodePos;
        currentReplaceRowType.current = 'image';
        // Use 'image' type which allows both images and videos for replacement
        setResourceModalType('image');
        setResourceModalOpen(true);
      } catch (err) {
        console.error('Failed to handle image replacement request', err);
      }
    };

    document.addEventListener('tiptap:image:requestReplace', handler);
    return () => document.removeEventListener('tiptap:image:requestReplace', handler);
  }, [editor, enableMediaUI]);

  // Listen for video replacement requests from drag context menu
  useEffect(() => {
    const handler = (event) => {
      if (!editor) return;
      if (!enableMediaUI) return;
      
      try {
        const nodePos = event.detail?.nodePos;
        if (typeof nodePos !== 'number' || nodePos < 0) {
          console.warn('[TiptapToolbar] Invalid nodePos in video replacement request:', nodePos);
          return;
        }
        
        const nodeAtPos = editor.state.doc.nodeAt(nodePos);
        if (!nodeAtPos || nodeAtPos.type.name !== 'video') {
          console.warn('[TiptapToolbar] Node at position is not a video:', nodeAtPos?.type?.name);
          return;
        }
        
        // Store the position for replacement
        currentReplaceRowPos.current = nodePos;
        currentReplaceRowType.current = 'video';
        // Use 'image' type which allows both images and videos for replacement
        // This allows replacing video with image or vice versa
        setResourceModalType('image');
        setResourceModalOpen(true);
      } catch (err) {
        console.error('Failed to handle video replacement request', err);
      }
    };

    document.addEventListener('tiptap:video:requestReplace', handler);
    return () => document.removeEventListener('tiptap:video:requestReplace', handler);
  }, [editor, enableMediaUI]);

  // Listen for carousel edit requests dispatched from:
  //   - the drag-context menu's "Edit carousel" button
  //   - the per-slide bubble menu's pencil button
  // Both surfaces send `{nodePos, slides}` so we can capture the carousel's
  // current state, then open ResourceModal in 'image' mode with the slides
  // threaded through `existingCarouselSlides` (which flips it to edit mode).
  useEffect(() => {
    const handler = (event) => {
      if (!editor) return;
      if (!enableMediaUI) return;
      try {
        const nodePos = event.detail?.nodePos;
        const slides = event.detail?.slides;
        if (typeof nodePos !== 'number' || nodePos < 0) {
          console.warn('[TiptapToolbar] Invalid nodePos in carousel edit request:', nodePos);
          return;
        }
        const nodeAtPos = editor.state.doc.nodeAt(nodePos);
        if (!nodeAtPos || nodeAtPos.type.name !== 'postImageCarousel') {
          console.warn('[TiptapToolbar] Node at position is not a carousel:', nodeAtPos?.type?.name);
          return;
        }
        currentCarouselEditPos.current = nodePos;
        setCurrentCarouselEditSlides(Array.isArray(slides) ? slides : []);
        setResourceModalType('image');
        setResourceModalOpen(true);
      } catch (err) {
        console.error('Failed to handle carousel edit request', err);
      }
    };
    document.addEventListener('tiptap:carousel:requestEdit', handler);
    return () => document.removeEventListener('tiptap:carousel:requestEdit', handler);
  }, [editor, enableMediaUI]);

  // Listen for per-slide replacement requests from the carousel bubble menu.
  // These open ResourceModal in regular 'image' mode (single-select). When
  // the user picks one resource, `handleResourceSelect` checks the slide-idx
  // ref and updates just that slide on the carousel node.
  useEffect(() => {
    const handler = (event) => {
      if (!editor) return;
      if (!enableMediaUI) return;
      try {
        const nodePos = event.detail?.nodePos;
        const slideIdx = event.detail?.slideIdx;
        if (typeof nodePos !== 'number' || nodePos < 0) return;
        if (typeof slideIdx !== 'number' || slideIdx < 0) return;
        const nodeAtPos = editor.state.doc.nodeAt(nodePos);
        if (!nodeAtPos || nodeAtPos.type.name !== 'postImageCarousel') return;
        currentReplaceRowPos.current = nodePos;
        currentReplaceRowType.current = 'carouselSlide';
        currentCarouselReplaceSlideIdx.current = slideIdx;
        const carouselSlides = Array.isArray(nodeAtPos.attrs?.slides) ? nodeAtPos.attrs.slides : [];
        currentCarouselReplaceSlide.current = carouselSlides[slideIdx] || null;
        currentCarouselReplaceLockedSlides.current = carouselSlides.filter((_, idx) => idx !== slideIdx);
        setResourceModalType('image');
        setResourceModalOpen(true);
      } catch (err) {
        console.error('Failed to handle carousel slide replacement request', err);
      }
    };
    document.addEventListener('tiptap:carouselSlide:requestReplace', handler);
    return () => document.removeEventListener('tiptap:carouselSlide:requestReplace', handler);
  }, [editor, enableMediaUI]);

  // Listen for resource tag edit requests from resource-node floating menus
  useEffect(() => {
    const handler = (event) => {
      const resourceId = event?.detail?.resourceId;
      // eslint-disable-next-line no-console
      console.log('[resource_tags] received tiptap:resource:requestTags', { resourceId, hasGlobalOpener: typeof window.AAOpenResourceTagsModal === 'function' });
      if (!resourceId) return;
      if (typeof window.AAOpenResourceTagsModal === 'function') {
        window.AAOpenResourceTagsModal(String(resourceId));
      } else {
        // Fallback for older bundles: keep compatibility with a global event.
        window.dispatchEvent(new CustomEvent("admin:resource:editTags", { detail: { resourceId: String(resourceId) } }));
      }
    };

    document.addEventListener('tiptap:resource:requestTags', handler);
    // eslint-disable-next-line no-console
    console.log('[resource_tags] listener installed for tiptap:resource:requestTags');
    return () => document.removeEventListener('tiptap:resource:requestTags', handler);
  }, []);

  // Listen for paste events to detect URLs, images, videos, and files
  useEffect(() => {
    // This handler is used for two separate behaviors:
    // - pasteResourceHandler: file paste + generic URL -> resource modal
    // - pasteVideoEmbeds: YouTube/Vimeo URL -> embedded video node
    if (!editor || (!enablePasteResourceHandler && !enableVideoEmbedOnPaste)) return;

    const handlePaste = (event) => {
      const clipboardData = event.clipboardData || window.clipboardData;
      if (!clipboardData) return;

      // Handle file paste only when the full resource paste handler is enabled.
      if (enablePasteResourceHandler) {
        // First, check for files (images, videos, or other files)
        const items = clipboardData.items;
        if (items && items.length > 0) {
          let hasImage = false;
          let hasVideo = false;
          let hasAudio = false;
          let hasFile = false;

          for (let i = 0; i < items.length; i++) {
            const item = items[i];
            if (item.kind === 'file') {
              const file = item.getAsFile();
              if (file) {
                if (file.type.startsWith('image/')) {
                  hasImage = true;
                } else if (file.type.startsWith('video/')) {
                  hasVideo = true;
                } else if (file.type.startsWith('audio/')) {
                  hasAudio = true;
                } else {
                  hasFile = true;
                }
              }
            }
          }

          // Only handle pasted files when the relevant features are enabled.
          const canHandleMedia = enableMediaUI && (hasImage || hasVideo);
          const canHandleAudio = enableResourceUI && enableFileUI && hasAudio;
          const canHandleDownloads = enableResourceUI && enableFileUI && hasFile;

          if (canHandleMedia || canHandleAudio || canHandleDownloads) {
            // Prevent default paste
            event.preventDefault();
            event.stopPropagation();

            // Collect all files
            const files = [];
            for (let i = 0; i < items.length; i++) {
              const item = items[i];
              if (item.kind === 'file') {
                const file = item.getAsFile();
                if (file) {
                  files.push(file);
                }
              }
            }

            // Store files and open appropriate resource modal
            if (canHandleMedia) {
              setInitialFiles(files);
              setResourceModalType('image'); // 'image' type handles both images and videos
              setResourceModalOpen(true);
            } else if (canHandleAudio) {
              setInitialFiles(files);
              setResourceModalType('audio');
              setResourceModalOpen(true);
            } else if (canHandleDownloads) {
              setInitialFiles(files);
              setResourceModalType('download');
              setResourceModalOpen(true);
            }
            return;
          }
        }
      }

      // If no files, check for pasted text (URLs)
      const pastedText = clipboardData.getData('text/plain');
      
      if (!pastedText || !pastedText.trim()) {
        return; // Let default paste handler handle it
      }

      if (clipboardPlainLooksLikeHtml(pastedText)) {
        return; // Editor paste handler parses HTML and strips inline formatting
      }

      const trimmedText = pastedText.trim();
      
      // Check if pasted text is a URL - more lenient detection
      const isUrl = trimmedText.startsWith('http://') || 
                    trimmedText.startsWith('https://') ||
                    trimmedText.startsWith('www.') ||
                    /^([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?/i.test(trimmedText);

      // If this is a YouTube/Vimeo URL and media is enabled, embed it immediately.
      if (isUrl && enableVideoEmbedOnPaste && enableMediaUI) {
        const parsed = parseEmbedProvider(trimmedText);
        if (parsed?.provider && parsed?.embedId) {
          // Prevent default paste
          event.preventDefault();
          event.stopPropagation();

          const beforePos = editor.state.selection.from;
          editor
            .chain()
            .focus()
            .insertContent({
              type: 'embedVideo',
              attrs: mergeBlockIndentAttrs(editor.state, {
                provider: parsed.provider,
                embedId: parsed.embedId,
                url: trimmedText,
                style: 'max-width:100%',
              }),
            })
            .run();

          // Move cursor to caption area of the inserted embed video
          setTimeout(() => {
            const { doc } = editor.state;
            let nodePos = -1;
            doc.descendants((node, pos) => {
              if (
                node.type.name === 'embedVideo' &&
                node.attrs.provider === parsed.provider &&
                node.attrs.embedId === parsed.embedId &&
                pos >= beforePos
              ) {
                nodePos = pos;
                return false; // stop iteration
              }
            });
            if (nodePos >= 0) {
              const captionPos = nodePos + 1;
              editor.chain().focus().setTextSelection(captionPos).run();
            }
          }, 10);

          return;
        }
      }

      // Generic URL -> resource "link" modal only when full resource paste handler is enabled.
      if (
        isUrl &&
        enablePasteResourceHandler &&
        enableResourceUI &&
        !selectionInRestrictedInlineLinkContext(editor.state)
      ) {
        // Prevent default paste
        event.preventDefault();
        event.stopPropagation();
        
        // Open external link modal with pasted URL
        setInitialLinkUrl(trimmedText);
        setResourceModalType('link');
        setResourceModalOpen(true);
      }
    };

    // Add paste handler to editor DOM
    const { dom } = editor.view;
    dom.addEventListener('paste', handlePaste, true); // Use capture phase

    return () => {
      dom.removeEventListener('paste', handlePaste, true);
    };
  }, [editor, enablePasteResourceHandler, enableVideoEmbedOnPaste, enableMediaUI, enableResourceUI, enableFileUI]);

  // Listen for drag and drop events to detect images, videos, and files
  useEffect(() => {
    if (!editor || !enablePasteResourceHandler) return;

    const handleDragOver = (event) => {
      // Check if dragging files
      if (event.dataTransfer && event.dataTransfer.types) {
        const types = Array.from(event.dataTransfer.types);
        if (types.includes('Files')) {
          event.preventDefault();
          event.stopPropagation();
        }
      }
    };

    const handleDrop = (event) => {
      const dataTransfer = event.dataTransfer;
      if (!dataTransfer || !dataTransfer.files || dataTransfer.files.length === 0) {
        return; // Let default drop handler handle it
      }

      const files = Array.from(dataTransfer.files);
      if (files.length === 0) return;

      // Prevent default drop
      event.preventDefault();
      event.stopPropagation();

      // Determine file types
      let hasImage = false;
      let hasVideo = false;
      let hasAudio = false;
      let hasFile = false;

      for (const file of files) {
        if (file.type.startsWith('image/')) {
          hasImage = true;
        } else if (file.type.startsWith('video/')) {
          hasVideo = true;
        } else if (file.type.startsWith('audio/')) {
          hasAudio = true;
        } else {
          hasFile = true;
        }
      }

      // Store files and open appropriate resource modal
      const canHandleMedia = enableMediaUI && (hasImage || hasVideo);
      const canHandleAudio = enableResourceUI && enableFileUI && hasAudio;
      const canHandleDownloads = enableResourceUI && enableFileUI && hasFile;

      if (canHandleMedia) {
        setInitialFiles(files);
        setResourceModalType('image'); // 'image' type handles both images and videos
        setResourceModalOpen(true);
      } else if (canHandleAudio) {
        setInitialFiles(files);
        setResourceModalType('audio');
        setResourceModalOpen(true);
      } else if (canHandleDownloads) {
        setInitialFiles(files);
        setResourceModalType('download');
        setResourceModalOpen(true);
      }
    };

    // Add drag and drop handlers to editor DOM
    const { dom } = editor.view;
    dom.addEventListener('dragover', handleDragOver, true);
    dom.addEventListener('drop', handleDrop, true);

    return () => {
      dom.removeEventListener('dragover', handleDragOver, true);
      dom.removeEventListener('drop', handleDrop, true);
    };
  }, [editor, enablePasteResourceHandler]);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    onFileUpload(file);
    event.target.value = '';
  };

  const handleResourceSelect = (resource, modalFileType) => {
    if (!editor || !resource) return;

    // Check if we're replacing an existing row
    const isReplacing = currentReplaceRowPos.current !== null && currentReplaceRowType.current !== null;
    const replacePos = currentReplaceRowPos.current;
    const replaceType = currentReplaceRowType.current;

    // Per-slide carousel replace: opened from the carousel bubble menu's
    // Replace button. We swap just the targeted slide's media URL/poster on
    // the existing carousel node and exit early — no insertion or other
    // node-replacement codepath should run for this case.
    if (isReplacing && replaceType === 'carouselSlide') {
      try {
        const slideIdx = currentCarouselReplaceSlideIdx.current;
        const carouselNode =
          replacePos !== null ? editor.state.doc.nodeAt(replacePos) : null;
        if (
          !carouselNode ||
          carouselNode.type.name !== 'postImageCarousel' ||
          typeof slideIdx !== 'number' ||
          slideIdx < 0
        ) {
          currentReplaceRowPos.current = null;
          currentReplaceRowType.current = null;
          currentCarouselReplaceSlideIdx.current = null;
          return;
        }

        // Same URL/poster rules as ResourceModal slidesFromResources (see carouselSlideFromResource).
        const preservedCaption = (carouselNode.attrs?.slides?.[slideIdx]?.caption) || '';
        const newSlide = carouselSlideFromResource(resource, { caption: preservedCaption });
        if (!newSlide) return;

        const oldSlides = Array.isArray(carouselNode.attrs?.slides)
          ? carouselNode.attrs.slides
          : [];
        let nextSlides = oldSlides.map((s, i) => (i === slideIdx ? newSlide : s));
        nextSlides = propagateCarouselSlideMediaToSharedResourceIds(nextSlides, newSlide);

        editor
          .chain()
          .focus()
          .command(({ tr, dispatch }) => {
            if (!dispatch) return false;
            tr.setNodeMarkup(replacePos, undefined, {
              ...carouselNode.attrs,
              slides: nextSlides,
            });
            return true;
          })
          .run();
      } catch (err) {
        console.error('[Replace] carousel slide replace failed:', err);
      } finally {
        currentReplaceRowPos.current = null;
        currentReplaceRowType.current = null;
        currentCarouselReplaceSlideIdx.current = null;
      }
      return;
    }

    // Determine file type and get appropriate URL based on modal context
    // If replacing, use replaceType to determine the target type
    // If selected from download extension, prioritize downloadable_file_attachment_url
    // If selected from image/video extension, prioritize display_image_file_url
    // If selected from link extension, use clickable_link
    let url = null;
    let isImage = false;
    let isVideo = false;
    let isDownload = false;
    let isLink = false;
    let isAudio = false;

    // If replacing, determine the target type based on the resource's available URLs
    // This allows replacing image with video or video with image
    if (isReplacing && (replaceType === 'image' || replaceType === 'video')) {
      // Helper function to check if URL is a video
      const isVideoUrl = (urlString) => {
        if (!urlString) return false;
        try {
          // First check the rsct (content type) parameter in the URL - most reliable
          const urlObj = new URL(urlString);
          const rsct = urlObj.searchParams.get('rsct');
          if (rsct && rsct.startsWith('video/')) {
            return true;
          }
          
          // Also check for video extensions in the filename
          // Decode URL to handle encoded filenames
          const decoded = decodeURIComponent(urlString);
          // Check for video extensions (case insensitive)
          const videoExtensions = /\.(mp4|webm|ogg|mov|avi|wmv|flv|mkv|m4v|3gp)(\?|$|&)/i;
          // Check both original and decoded URL
          return videoExtensions.test(urlString) || videoExtensions.test(decoded);
        } catch (e) {
          // If URL parsing fails, check for video extensions in the string
          const videoExtensions = /\.(mp4|webm|ogg|mov|avi|wmv|flv|mkv|m4v|3gp)(\?|$|&)/i;
          // Also check for rsct=video/ in the URL string
          if (/rsct=video\//i.test(urlString)) {
            return true;
          }
          return videoExtensions.test(urlString);
        }
      };
      
      // When replacing media, prefer the *actual media file* URL as the node src.
      // For VIDEO resources that also have a cover image, `display_image_file_url`
      // is the thumbnail and must NOT become the video src.
      if (resource.file_type === 'VIDEO') {
        if (resource.downloadable_file_attachment_url) {
          isVideo = true;
          url = resource.downloadable_file_attachment_url;
        } else if (resource.display_image_file_url && isVideoUrl(resource.display_image_file_url)) {
          // Backward-compat fallback: some older resources stored video under display_image_file_url
          isVideo = true;
          url = resource.display_image_file_url;
        }
      } else if (resource.file_type === 'IMAGE') {
        if (resource.downloadable_file_attachment_url) {
          isImage = true;
          url = resource.downloadable_file_attachment_url;
        } else if (resource.display_image_file_url) {
          isImage = true;
          url = resource.display_image_file_url;
        }
      } else {
        // Last-resort heuristic
        if (resource.downloadable_file_attachment_url) {
          if (isVideoUrl(resource.downloadable_file_attachment_url)) {
            isVideo = true;
            url = resource.downloadable_file_attachment_url;
          } else {
            isImage = true;
            url = resource.downloadable_file_attachment_url;
          }
        } else if (resource.display_image_file_url) {
          if (isVideoUrl(resource.display_image_file_url)) {
            isVideo = true;
            url = resource.display_image_file_url;
          } else {
            isImage = true;
            url = resource.display_image_file_url;
          }
        }
      }
    } else if (modalFileType === 'link') {
      // If selected from link extension, use clickable_link
      if (resource.clickable_link) {
        isLink = true;
        url = resource.clickable_link;
      }
    } else if (modalFileType === 'audio') {
      // If selected from audio extension, use downloadable_file_attachment (media, not in recently added)
      if (resource.downloadable_file_attachment_url) {
        isAudio = true;
        url = resource.downloadable_file_attachment_url;
      }
    } else if (modalFileType === 'download') {
      // If selected from download extension, prioritize downloadable_file_attachment
      if (resource.downloadable_file_attachment_url) {
        isDownload = true;
        url = resource.downloadable_file_attachment_url;
      } else if (resource.display_image_file_url) {
        // Fallback to display_image_file if downloadable_file_attachment doesn't exist
        if (resource.file_type === 'VIDEO') {
          isVideo = true;
        } else {
          isImage = true;
        }
        url = resource.display_image_file_url;
      }
    } else {
      // Selected from image/video extension - check downloadable_file_attachment
      // (since we now use downloadable_file_attachment for images/videos)
      if (resource.downloadable_file_attachment_url) {
        // Check file_type to determine if it's an image or video
        if (resource.file_type === 'VIDEO') {
          isVideo = true;
          url = resource.downloadable_file_attachment_url;
        } else if (resource.file_type === 'IMAGE') {
          isImage = true;
          url = resource.downloadable_file_attachment_url;
        } else if (resource.file_type === 'AUDIO') {
          isAudio = true;
          url = resource.downloadable_file_attachment_url;
        } else {
          // If it's not an image or video or audio, treat as download
          isDownload = true;
          url = resource.downloadable_file_attachment_url;
        }
      } else if (resource.display_image_file_url) {
        // Fallback to display_image_file for backward compatibility
        if (resource.file_type === 'VIDEO') {
          isVideo = true;
          url = resource.display_image_file_url;
        } else {
          isImage = true;
          url = resource.display_image_file_url;
        }
      }
    }

    if (!url) return;

    // If replacing an existing row, update it instead of inserting
    // Use the same logic as the existing downloadRow replacement
    let replacementSucceeded = false;
    const wasReplacing = isReplacing && replacePos !== null;
    if (wasReplacing) {
      try {
        // replacePos is already the node position (set when double-clicking)
        // Verify it's still valid and points to the correct node type
        const nodeAtPos = editor.state.doc.nodeAt(replacePos);
        
        if (!nodeAtPos) {
          console.warn('[Replace] Node not found at replacePos:', replacePos);
          currentReplaceRowPos.current = null;
          currentReplaceRowType.current = null;
          replacementSucceeded = false;
          return;
        }
        
        const nodeType = nodeAtPos.type.name;
        
        // Verify it's a supported node type for replacement
        const supportedReplaceTypes = ['downloadRow', 'externalLinkRow', 'image', 'video', 'audioRow'];
        if (!supportedReplaceTypes.includes(nodeType)) {
          console.warn('[Replace] Node at replacePos is not a supported type for replacement, it is:', nodeType);
          currentReplaceRowPos.current = null;
          currentReplaceRowType.current = null;
          replacementSucceeded = false;
          return;
        }
        
        if (replaceType === 'download' && nodeType === 'downloadRow') {
          // For download replacement, we need to get the URL from the resource
          // Check if resource has downloadable_file_attachment_url or use href from resource
          const downloadUrl = resource.downloadable_file_attachment_url || resource.href || url;
          const titleText = resource.title || resource.downloadable_file_filename || 'Click to edit title';

          // Replace the entire node in a single transaction
          try {
            // Create the new node
            const newNode = editor.schema.nodes.downloadRow.create(
              {
                href: downloadUrl,
                filename: titleText,
                contentType: resource.downloadable_file_content_type || null,
                signedId: resource.downloadable_file_signed_id || null,
                resourceId: resource.id
              },
              [
                editor.schema.nodes.paragraph.create(
                  {},
                  titleText ? editor.schema.text(titleText) : null
                )
              ]
            );
            
            // Replace the entire node using replaceWith
            const nodeEnd = replacePos + nodeAtPos.nodeSize;
            
            editor.chain()
              .focus()
              .command(({ tr, dispatch }) => {
                if (!dispatch) return false;
                tr.replaceWith(replacePos, nodeEnd, newNode);
                return true;
              })
              .run();
            
            // Reset replacement tracking
            currentReplaceRowPos.current = null;
            currentReplaceRowType.current = null;
            replacementSucceeded = true;
            return; // Exit early to prevent insertion
          } catch (e) {
            console.error('Failed to replace downloadRow:', e);
            // Reset tracking on error
            currentReplaceRowPos.current = null;
            currentReplaceRowType.current = null;
            replacementSucceeded = false;
            // Return early to prevent insertion - wasReplacing will catch this
            return;
          }
        } else if (replaceType === 'link' && nodeType === 'externalLinkRow') {
          // Replace the entire node in a single transaction (same approach as downloadRow)
          const titleText = resource.title || 'Click to edit title';
          
          try {
            // Create the new node
            const newNode = editor.schema.nodes.externalLinkRow.create(
              {
                href: url,
                resourceId: resource.id
              },
              [
                editor.schema.nodes.paragraph.create(
                  {},
                  titleText ? editor.schema.text(titleText) : null
                )
              ]
            );
            
            // Replace the entire node using replaceWith
            const nodeEnd = replacePos + nodeAtPos.nodeSize;
            
            editor.chain()
              .focus()
              .command(({ tr, dispatch }) => {
                if (!dispatch) return false;
                tr.replaceWith(replacePos, nodeEnd, newNode);
                return true;
              })
              .run();
            
            // Reset replacement tracking
            currentReplaceRowPos.current = null;
            currentReplaceRowType.current = null;
            replacementSucceeded = true;
            return; // Exit early to prevent insertion
          } catch (e) {
            console.error('Failed to replace externalLinkRow:', e);
            // Reset tracking on error
            currentReplaceRowPos.current = null;
            currentReplaceRowType.current = null;
            replacementSucceeded = false;
            // Return early to prevent insertion - wasReplacing will catch this
            return;
          }
        } else if ((replaceType === 'image' || replaceType === 'video') && (nodeType === 'image' || nodeType === 'video')) {
          // Replace image or video node (supports cross-type replacement: image->video or video->image)
          try {
            const nodeEnd = replacePos + nodeAtPos.nodeSize;
            
            editor.chain()
              .focus()
              .command(({ tr, dispatch }) => {
                if (!dispatch) return false;
                
                if (isImage) {
                  // Replace with image node (even if original was video)
                  const newNode = editor.schema.nodes.image.create({
                    src: url,
                    resourceId: resource.id
                  });
                  tr.replaceWith(replacePos, nodeEnd, newNode);
                } else if (isVideo) {
                  // Replace with video node (even if original was image)
                  const posterUrl = resource.display_image_file_url || null;
                  const newNode = editor.schema.nodes.video.create({
                    src: url,
                    controls: true,
                    style: 'max-width:100%',
                    resourceId: resource.id,
                    poster: posterUrl
                  });
                  tr.replaceWith(replacePos, nodeEnd, newNode);
                } else {
                  console.warn('[Replace] Neither isImage nor isVideo is true, cannot replace');
                  return false;
                }
                
                return true;
              })
              .run();
            
            // Reset replacement tracking
            currentReplaceRowPos.current = null;
            currentReplaceRowType.current = null;
            replacementSucceeded = true;
            return;
          } catch (e) {
            console.error('Failed to replace media:', e);
            currentReplaceRowPos.current = null;
            currentReplaceRowType.current = null;
            replacementSucceeded = false;
            return;
          }
        } else if (replaceType === 'audio' && nodeType === 'audioRow') {
          const audioUrl = resource.downloadable_file_attachment_url || resource.href || url;
          const titleText = resource.title || resource.downloadable_file_filename || 'Click to edit title';
          try {
            const newNode = editor.schema.nodes.audioRow.create(
              {
                href: audioUrl,
                filename: titleText,
                contentType: resource.downloadable_file_content_type || null,
                signedId: resource.downloadable_file_signed_id || null,
                resourceId: resource.id
              },
              [
                editor.schema.nodes.paragraph.create(
                  {},
                  titleText ? editor.schema.text(titleText) : null
                )
              ]
            );
            const nodeEnd = replacePos + nodeAtPos.nodeSize;
            editor.chain()
              .focus()
              .command(({ tr, dispatch }) => {
                if (!dispatch) return false;
                tr.replaceWith(replacePos, nodeEnd, newNode);
                return true;
              })
              .run();
            currentReplaceRowPos.current = null;
            currentReplaceRowType.current = null;
            replacementSucceeded = true;
            return;
          } catch (e) {
            console.error('Failed to replace audioRow:', e);
            currentReplaceRowPos.current = null;
            currentReplaceRowType.current = null;
            replacementSucceeded = false;
            return;
          }
        } else {
          // Node type doesn't match replaceType
          console.warn('[Replace] Node type mismatch. replaceType:', replaceType, 'nodeType:', nodeType);
          currentReplaceRowPos.current = null;
          currentReplaceRowType.current = null;
          replacementSucceeded = false;
          return;
        }
      } catch (err) {
        console.error('Failed to replace row:', err);
        // Reset tracking on error to prevent issues
        currentReplaceRowPos.current = null;
        currentReplaceRowType.current = null;
        // Fall through to normal insertion if replacement fails
      }
    }
    
    // If we were in replacement mode, don't continue with normal insertion
    // (even if replacement failed, we don't want to insert a new row)
    if (wasReplacing) {
      // Reset tracking if replacement didn't succeed
      if (!replacementSucceeded) {
        currentReplaceRowPos.current = null;
        currentReplaceRowType.current = null;
      }
      return;
    }

    if (isImage) {
      const beforePos = editor.state.selection.from;
      editor.chain().focus().setImage({ 
        src: url,
        resourceId: resource.id 
      }).run();
      // Move cursor to caption area of the inserted image
      setTimeout(() => {
        const { doc } = editor.state;
        // Find the image node we just inserted
        let imagePos = -1;
        doc.descendants((node, pos) => {
          if (node.type.name === 'image' && node.attrs.src === url && pos >= beforePos) {
            imagePos = pos;
            return false; // stop iteration
          }
        });
        if (imagePos >= 0) {
          // Move cursor to caption area (position after image node start, inside caption)
          const captionPos = imagePos + 1;
          editor.chain().focus().setTextSelection(captionPos).run();
        }
      }, 10);
    } else if (isVideo) {
      const beforePos = editor.state.selection.from;
      const posterUrl = resource.display_image_file_url || null;
      editor.chain().focus().insertContent({
        type: 'video',
        attrs: mergeBlockIndentAttrs(editor.state, {
          src: url,
          controls: true,
          style: 'max-width:100%',
          resourceId: resource.id,
          poster: posterUrl,
        }),
      }).run();
      // Move cursor to caption area of the inserted video
      setTimeout(() => {
        const { doc } = editor.state;
        // Find the video node we just inserted
        let videoPos = -1;
        doc.descendants((node, pos) => {
          if (node.type.name === 'video' && node.attrs.src === url && pos >= beforePos) {
            videoPos = pos;
            return false; // stop iteration
          }
        });
        if (videoPos >= 0) {
          // Move cursor to caption area (position after video node start, inside caption)
          const captionPos = videoPos + 1;
          editor.chain().focus().setTextSelection(captionPos).run();
        }
      }, 10);
    } else if (isDownload) {
      const insertCtx = resolveResourceTableRowInsert(editor.state);
      if (insertCtx) {
        const { insertPos, tablePos } = insertCtx;
        editor.chain().focus().setTextSelection(insertPos).insertContent({
          type: 'downloadRow',
          attrs: {
            href: url,
            filename: resource.title,
            contentType: null,
            signedId: null,
            resourceId: resource.id
          },
          content: [
            {
              type: 'paragraph',
              content: [
                {
                  type: 'text',
                  text: resource.title || 'Click to edit title'
                }
              ]
            }
          ]
        }).run();
        afterResourceRowInsertFocus(editor, editor.state, {
          tablePos,
          insertPos,
          resourceId: resource.id,
          rowType: 'downloadRow',
        });
      } else {
        // Create new resourceTable with downloadRow using setDownload
        const beforePos = editor.state.selection.from;
        editor.chain().focus().setDownload({
          href: url,
          filename: resource.title,
          contentType: null,
          signedId: null,
          resourceId: resource.id,
          title: resource.title || 'Click to edit title'
        }).run();
        afterResourceRowInsertFocus(editor, editor.state, {
          resourceId: resource.id,
          rowType: 'downloadRow',
          insertPos: beforePos,
        });
      }
    } else if (isAudio) {
      const insertCtx = resolveResourceTableRowInsert(editor.state);
      if (insertCtx) {
        const { insertPos, tablePos } = insertCtx;
        editor.chain().focus().setTextSelection(insertPos).insertContent({
          type: 'audioRow',
          attrs: {
            href: url,
            filename: resource.title || resource.downloadable_file_filename,
            contentType: resource.downloadable_file_content_type || null,
            signedId: resource.downloadable_file_signed_id || null,
            resourceId: resource.id
          },
          content: [
            {
              type: 'paragraph',
              content: [
                {
                  type: 'text',
                  text: resource.title || 'Click to edit title'
                }
              ]
            }
          ]
        }).run();
        afterResourceRowInsertFocus(editor, editor.state, {
          tablePos,
          insertPos,
          resourceId: resource.id,
          rowType: 'audioRow',
        });
      } else {
        const beforePos = editor.state.selection.from;
        editor.chain().focus().setAudio({
          href: url,
          filename: resource.title || resource.downloadable_file_filename,
          contentType: resource.downloadable_file_content_type || null,
          signedId: resource.downloadable_file_signed_id || null,
          resourceId: resource.id,
          title: resource.title || 'Click to edit title'
        }).run();
        afterResourceRowInsertFocus(editor, editor.state, {
          resourceId: resource.id,
          rowType: 'audioRow',
          insertPos: beforePos,
        });
      }
    } else if (isLink) {
      const insertCtx = resolveResourceTableRowInsert(editor.state);
      if (insertCtx) {
        const { insertPos, tablePos } = insertCtx;
        editor.chain().focus().setTextSelection(insertPos).insertContent({
          type: 'externalLinkRow',
          attrs: {
            href: url,
            resourceId: resource.id
          },
          content: [
            {
              type: 'paragraph',
              content: [
                {
                  type: 'text',
                  text: resource.title || 'Click to edit title'
                }
              ]
            }
          ]
        }).run();
        afterResourceRowInsertFocus(editor, editor.state, {
          tablePos,
          insertPos,
          resourceId: resource.id,
          rowType: 'externalLinkRow',
        });
      } else {
        // Create new resourceTable with externalLinkRow using setExternalLink
        const beforePos = editor.state.selection.from;
        editor.chain().focus().setExternalLink({
          href: url,
          resourceId: resource.id,
          title: resource.title || 'Click to edit title'
        }).run();
        afterResourceRowInsertFocus(editor, editor.state, {
          resourceId: resource.id,
          rowType: 'externalLinkRow',
          insertPos: beforePos,
        });
      }
    }
  };

  // Upload handler for the dedicated download input. Uses direct upload to Azure
  // and updates the previously-inserted downloadRow node with the returned
  // metadata (href, filename, contentType, signedId).
  const handleDownloadSelect = async (event) => {
    const file = event.target.files[0];
    // Clear selection so same file can be chosen again
    event.target.value = '';
    if (!file) return;
    if (!editor) return;

    const plan = currentDownloadInsertPlan.current;
    const placeholderPos = currentDownloadInsertPos.current;
    // Clear pending plan immediately (we'll still use placeholderPos if present)
    currentDownloadInsertPlan.current = null;
    if (!plan && (placeholderPos == null)) {
      console.warn('No pending download insertion plan or placeholder position found for upload');
      return;
    }

    setIsUploadingDownload(true);
    try {
      // Use direct upload to Azure (client-side upload)
      const result = await uploadToAzure(file, {
        onProgress: (progress) => {
          console.log(`[DirectUpload] Download file progress: ${Math.round(progress)}%`);
        }
      });
      
      const url = result.url;
      const filename = result.filename || file.name;
      const contentType = result.content_type || file.type || null;
      const signedId = result.signed_id || null;
      if (!url) {
        throw new Error('Upload completed but no URL returned');
      }
      // If the plan says to update an existing row (replace file), update it
      if (plan && plan.type === 'updateRow' && typeof plan.pos === 'number') {
        // Update the node attributes first
        editor.chain().focus().setTextSelection(plan.pos).updateAttributes('downloadRow', {
          href: url,
          filename: filename,
          contentType: contentType,
          signedId: signedId,
        }).run();

        // Also replace the inner paragraph content with the filename so the
        // visible title updates immediately. Compute the paragraph range
        // from the node size.
        const nodeAtPos = editor.state.doc.nodeAt(plan.pos);
        if (nodeAtPos) {
          let from = plan.pos + 1;
          let to = plan.pos + nodeAtPos.nodeSize - 1;
          let titleRangeFound = false;
          nodeAtPos.forEach((child, offset) => {
            if (titleRangeFound || child.type.name !== 'paragraph') return;
            if (paragraphRowRole(child) === 'subtitle') return;
            from = plan.pos + 1 + offset;
            to = from + child.nodeSize;
            titleRangeFound = true;
          });
          const titleText = filename || (plan.attrs && plan.attrs.filename) || 'Click to edit title';
          try {
            editor.chain().focus().insertContentAt({ from, to }, [
              {
                type: 'paragraph',
                attrs: RESOURCE_ROW_TITLE_PARAGRAPH_ATTRS,
                content: [{ type: 'text', text: titleText }],
              },
            ]).run();
          } catch (e) {
            // Fallback: ignore if insertContentAt isn't available or fails
            console.warn('Failed to replace downloadRow paragraph content', e);
          }
        }
        afterResourceRowInsertFocus(editor, editor.state, {
          insertPos: plan.pos,
          rowType: 'downloadRow',
        });
      // If the plan says to insert at a specific pos, insert the row now
      } else if (plan && plan.type === 'insertAt' && typeof plan.pos === 'number') {
        editor.chain().focus().setTextSelection(plan.pos).insertContent({
          type: 'downloadRow',
          attrs: {
            href: url,
            filename: filename,
            contentType: contentType,
            signedId: signedId,
          },
          content: [
            {
              type: 'paragraph',
              content: [
                {
                  type: 'text',
                  text: filename || 'Click to edit title'
                }
              ]
            }
          ]
        }).run();
        afterResourceRowInsertFocus(editor, editor.state, {
          insertPos: plan.pos,
          rowType: 'downloadRow',
        });
      } else if (plan && plan.type === 'createTable') {
        // Create a new download table and set the new row's href via the
        // setDownload helper (pass href so the created row contains it).
        const beforePos = editor.state.selection.from;
        editor.chain().focus().setDownload({ 
          href: url,
          filename: filename,
          contentType: contentType,
          signedId: signedId,
        }).run();
        afterResourceRowInsertFocus(editor, editor.state, {
          rowType: 'downloadRow',
          insertPos: beforePos,
        });
      } else {
        // Fallback: try to update an existing placeholder position if present
        const pos = placeholderPos != null ? placeholderPos : currentDownloadInsertPos.current;
        if (pos != null) {
          currentDownloadInsertPos.current = null;
          editor.chain().focus().setTextSelection(pos).updateAttributes('downloadRow', {
            href: url,
            filename: filename,
            contentType: contentType,
            signedId: signedId,
          }).run();
        }
      }
    } catch (err) {
      console.error('Download upload failed', err);
      alert('Upload failed: ' + (err.message || err));
    } finally {
      setIsUploadingDownload(false);
    }
  };

  const applyToolbarColor = (value) => {
    if (!editor) return;
    const ctx = resolvePostEditorColorPickerContext(toolbarState, { featuresPreset });
    const M = POST_EDITOR_COLOR_PICKER_MODES;
    switch (ctx.mode) {
      case M.heading:
        editor.chain().focus().setPostParagraphStyle({ style: 'heading', accent: value }).run();
        break;
      case M.subheading:
        editor.chain().focus().setPostParagraphStyle({ style: 'subheading', accent: value }).run();
        break;
      case M.quote:
        editor.chain().focus().setPathstoneQuoteVariant(value).run();
        break;
      case M.orderedList:
        editor
          .chain()
          .focus()
          .updateAttributes('orderedList', { markerColor: value })
          .run();
        break;
      default: {
        const token = normalizePsTextColorToken(value);
        if (token) editor.chain().focus().toggleMark('psTextColor', { token }).run();
        break;
      }
    }
  };

  const handleFontFamilyChange = (event) => {
    const className = event.target.value;
    if (className) {
      editor.chain().focus().unsetFontFamilyClass().setFontFamilyClass(className).run();
    }
  };

  const toggleLink = () => {
    const imageAttrs = editor.getAttributes('image');
    if (!imageAttrs.src && isPostEditorInlineLinkLocked(toolbarState)) {
      if (!editor.isActive('link')) return;
      const url = prompt('Enter a URL', editor.getAttributes('link').href || 'https://');
      if (url === null) return;
      if (url === '') {
        editor.chain().focus().unsetLink().run();
      }
      return;
    }

    // Check if current selection is an image
    if (imageAttrs.src) {
      // Handle image link
      const previousUrl = imageAttrs.href;
      const url = prompt('Enter a URL', previousUrl || 'https://');
      if (url === null) return;
      if (url === '') {
        editor.chain().focus().updateAttributes('image', { href: null }).run();
      } else {
        editor.chain().focus().updateAttributes('image', { href: url }).run();
      }
    } else {
      // Handle text link (existing logic)
      const previousUrl = editor.getAttributes('link').href;
      const url = prompt('Enter a URL', previousUrl || 'https://');
      if (url === null) return;
      if (url === '') {
        editor.chain().focus().unsetLink().run();
        return;
      }
      editor.chain().focus().setLink({ href: url }).run();
    }
  };

  // (Tags modal is intentionally NOT part of this toolbar; it lives on the resource-node floating menus.)

  const insertDownload = () => {
    const url = prompt('Enter URL (optional):');
    
    const { $from } = editor.state.selection;
    const insertCtx = resolveResourceTableRowInsert(editor.state);

    if (insertCtx) {
      editor.chain().focus().setTextSelection(insertCtx.insertPos).insertContent({
        type: 'downloadRow',
        attrs: {
          href: url && url.trim() !== '' && url !== 'https://' ? url.trim() : null,
        },
        content: [
          {
            type: 'paragraph',
            content: [
              {
                type: 'text',
                text: 'Click to edit title'
              }
            ]
          }
        ]
      }).run();
      afterResourceRowInsertFocus(editor, editor.state, {
        insertPos: insertCtx.insertPos,
        tablePos: insertCtx.tablePos,
        rowType: 'downloadRow',
      });
    } else {
      // Check nodes before and after cursor
      const before = $from.nodeBefore;
      const after = $from.nodeAfter;
      
      if (before && before.type.name === 'resourceTable') {
        // Find the end of the table before cursor
        const beforePos = $from.pos - before.nodeSize;
        const beforeTableEndPos = beforePos + before.nodeSize - 1;
        
        editor.chain().focus().setTextSelection(beforeTableEndPos).insertContent({
          type: 'downloadRow',
          attrs: {
            href: url && url.trim() !== '' && url !== 'https://' ? url.trim() : null,
          },
          content: [
            {
              type: 'paragraph',
              content: [
                {
                  type: 'text',
                  text: 'Click to edit title'
                }
            ]
          }
        ]
      }).run();
        afterResourceRowInsertFocus(editor, editor.state, {
          insertPos: beforeTableEndPos,
          tablePos: beforePos,
          rowType: 'downloadRow',
        });
      } else if (after && after.type.name === 'resourceTable') {
        // Find the end of the table after cursor
        const afterPos = $from.pos;
        const afterTableEndPos = afterPos + after.nodeSize - 1;
        
        editor.chain().focus().setTextSelection(afterTableEndPos).insertContent({
          type: 'downloadRow',
          attrs: {
            href: url && url.trim() !== '' && url !== 'https://' ? url.trim() : null,
          },
          content: [
            {
              type: 'paragraph',
              content: [
                {
                  type: 'text',
                  text: 'Click to edit title'
                }
              ]
            }
          ]
        }).run();
        afterResourceRowInsertFocus(editor, editor.state, {
          insertPos: afterTableEndPos,
          tablePos: afterPos,
          rowType: 'downloadRow',
        });
      } else {
        // Create new download table
        const beforePos = editor.state.selection.from;
        editor.chain().focus().setDownload({
          href: url && url.trim() !== '' && url !== 'https://' ? url.trim() : null,
        }).run();
        afterResourceRowInsertFocus(editor, editor.state, {
          rowType: 'downloadRow',
          insertPos: beforePos,
        });
      }
    }
  };

  // New helper: compute an upload-first insertion plan, then open the
  // dedicated download input (accepts all files). We do NOT insert a
  // placeholder; instead we wait for the upload to complete and then
  // perform the insertion/update based on the plan.
  const insertDownloadUploader = () => {
    const { $from } = editor.state.selection;
    const insertCtx = resolveResourceTableRowInsert(editor.state);

    if (insertCtx) {
      const { insertPos } = insertCtx;
      currentDownloadInsertPlan.current = { type: 'insertAt', pos: insertPos };
      try {
        editor.chain().focus().setTextSelection(insertPos).setFileUploadNode().run();
      } catch (e) {
        console.error('Failed to insert download uploader node', e);
        // Fallback: open native file picker as before
        if (downloadInputRef.current) {
          downloadInputRef.current.accept = '*/*';
          downloadInputRef.current.click();
        }
      }
      return;
    }

    const before = $from.nodeBefore;
    const after = $from.nodeAfter;

    if (before && before.type.name === 'resourceTable') {
      const beforePos = $from.pos - before.nodeSize;
      const beforeTableEndPos = beforePos + before.nodeSize - 1;
      currentDownloadInsertPlan.current = { type: 'insertAt', pos: beforeTableEndPos };
      try {
        editor.chain().focus().setTextSelection(beforeTableEndPos).setFileUploadNode().run();
      } catch (e) {
        console.error('Failed to insert download uploader node', e);
        if (downloadInputRef.current) {
          downloadInputRef.current.accept = '*/*';
          downloadInputRef.current.click();
        }
      }
      return;
    }

    if (after && after.type.name === 'resourceTable') {
      const afterPos = $from.pos;
      const afterTableEndPos = afterPos + after.nodeSize - 1;
      currentDownloadInsertPlan.current = { type: 'insertAt', pos: afterTableEndPos };
      try {
        editor.chain().focus().setTextSelection(afterTableEndPos).setFileUploadNode().run();
      } catch (e) {
        console.error('Failed to insert download uploader node', e);
        if (downloadInputRef.current) {
          downloadInputRef.current.accept = '*/*';
          downloadInputRef.current.click();
        }
      }
      return;
    }

    // No existing resource table context: create a table after successful upload
    currentDownloadInsertPlan.current = { type: 'createTable' };
    try {
      // Insert uploader at current selection; toolbar will convert on success
      editor.chain().focus().setFileUploadNode().run();
    } catch (e) {
      console.error('Failed to insert download uploader node', e);
      if (downloadInputRef.current) {
        downloadInputRef.current.accept = '*/*';
        downloadInputRef.current.click();
      }
    }
  };

  // Listen for successful uploads coming from ImageUploadNode when it's
  // used as a temporary uploader for the Download flow. The node will
  // dispatch a `tiptap:fileUpload:success` CustomEvent with detail:
  // { url, filename, contentType, signedId, nodePos }
  useEffect(() => {
    const handler = (event) => {
      if (!editor) return
      const detail = event?.detail || {}
      const { url, filename, contentType, signedId } = detail

      const plan = currentDownloadInsertPlan.current
      const placeholderPos = currentDownloadInsertPos.current
      currentDownloadInsertPlan.current = null

      if (!plan && (placeholderPos == null)) {
        console.warn('No pending download insertion plan found for fileUpload success')
        return
      }

      try {
        if (plan && plan.type === 'updateRow' && typeof plan.pos === 'number') {
          // For updateRow (replacement), we need to find the fileUpload node and replace it with a downloadRow
          // The fileUpload node should be at approximately the same position
          let fileUploadPos = null;
          let fileUploadNode = null;
          
          // Search around the original position for the fileUpload node
          for (let searchPos = Math.max(0, plan.pos - 10); searchPos <= plan.pos + 10; searchPos++) {
            try {
              const nodeAtSearchPos = editor.state.doc.nodeAt(searchPos);
              if (nodeAtSearchPos && nodeAtSearchPos.type.name === 'fileUpload') {
                fileUploadPos = searchPos;
                fileUploadNode = nodeAtSearchPos;
                break;
              }
            } catch (e) {
              // Continue searching if position is invalid
              continue;
            }
          }
          
          if (fileUploadNode && fileUploadPos !== null) {
            // Replace the fileUpload node with a downloadRow
            const nodeSize = fileUploadNode.nodeSize;
            editor.chain().focus().setDownload({
            href: url,
            filename: filename,
            contentType: contentType,
            signedId: signedId,
          }).run()
          } else {
            console.warn('Could not find fileUpload node to replace with downloadRow');
          }
        } else if (plan && plan.type === 'insertAt' && typeof plan.pos === 'number') {
          editor.chain().focus().setTextSelection(plan.pos).insertContent({
            type: 'downloadRow',
            attrs: {
              href: url,
              filename: filename,
              contentType: contentType,
              signedId: signedId,
            },
            content: [
              {
                type: 'paragraph',
                content: [
                  {
                    type: 'text',
                    text: filename || 'Click to edit title',
                  },
                ],
              },
            ],
          }).run()
          afterResourceRowInsertFocus(editor, editor.state, {
            insertPos: plan.pos,
            rowType: 'downloadRow',
          })
        } else if (plan && plan.type === 'createTable') {
          const beforePos = editor.state.selection.from
          editor.chain().focus().setDownload({
            href: url,
            filename: filename,
            contentType: contentType,
            signedId: signedId,
          }).run()
          afterResourceRowInsertFocus(editor, editor.state, {
            rowType: 'downloadRow',
            insertPos: beforePos,
          })
        } else {
          const pos = placeholderPos != null ? placeholderPos : currentDownloadInsertPos.current
          if (pos != null) {
            currentDownloadInsertPos.current = null
            editor.chain().focus().setTextSelection(pos).updateAttributes('downloadRow', {
              href: url,
              filename: filename,
              contentType: contentType,
              signedId: signedId,
            }).run()
            afterResourceRowInsertFocus(editor, editor.state, {
              insertPos: pos,
              rowType: 'downloadRow',
            })
          }
        }
      } catch (err) {
        console.error('Failed to apply fileUpload success to downloadRow', err)
      }
    }

    document.addEventListener('tiptap:fileUpload:success', handler)
    return () => document.removeEventListener('tiptap:fileUpload:success', handler)
  }, [editor])

  const insertExternalLink = () => {
    // Open ResourceModal for links
    setResourceModalType('link');
    setResourceModalOpen(true);
    return;
    
    // Old prompt-based code (kept for reference, but now using modal)
    const url = prompt('Enter URL (optional):');
    if (url === null) return;

    const { $from } = editor.state.selection;
    let foundResourceTable = false;
    let tablePos = null;
    
    // Check if we're inside any resource table
    for (let depth = $from.depth; depth >= 0; depth--) {
      const node = $from.node(depth);
      if (node.type.name === 'resourceTable') {
        tablePos = $from.start(depth);
        foundResourceTable = true;
        break;
      }
    }
    
    if (foundResourceTable && tablePos !== null) {
      // Check if we're inside a specific row (downloadRow or externalLinkRow)
      let insertAfterCurrentRow = false;
      let currentRowPos = null;
      
      for (let depth = $from.depth; depth >= 0; depth--) {
        const node = $from.node(depth);
        if (node.type.name === 'downloadRow' || node.type.name === 'externalLinkRow' || node.type.name === 'meetingRow') {
          currentRowPos = $from.start(depth);
          insertAfterCurrentRow = true;
          break;
        }
      }
      
      let insertPos;
      if (insertAfterCurrentRow && currentRowPos !== null) {
        // Insert after the current row
        const currentRowNode = editor.state.doc.nodeAt(currentRowPos);
        insertPos = currentRowPos + currentRowNode.nodeSize;
      } else {
        // Insert at the end of the table
        const tableNode = editor.state.doc.nodeAt(tablePos);
        insertPos = tablePos + tableNode.nodeSize - 1;
      }
      editor.chain().focus().setTextSelection(insertPos).insertContent({
        type: 'externalLinkRow',
        attrs: {
          href: url && url.trim() !== '' && url !== 'https://' ? url.trim() : null,
        },
        content: [
          {
            type: 'paragraph',
            content: [
              {
                type: 'text',
                text: 'Click to edit title'
              }
            ]
          }
        ]
      }).run();
    } else {
      // Check nodes before and after cursor
      const before = $from.nodeBefore;
      const after = $from.nodeAfter;
      
      if (before && before.type.name === 'resourceTable') {
        // Find the end of the table before cursor
        const beforePos = $from.pos - before.nodeSize;
        const beforeTableEndPos = beforePos + before.nodeSize - 1;
        editor.chain().focus().setTextSelection(beforeTableEndPos).insertContent({
          type: 'externalLinkRow',
          attrs: {
            href: url && url.trim() !== '' && url !== 'https://' ? url.trim() : null,
          },
          content: [
            {
              type: 'paragraph',
              content: [
                {
                  type: 'text',
                  text: 'Click to edit title'
                }
              ]
            }
          ]
        }).run();
      } else if (after && after.type.name === 'resourceTable') {
        // Find the end of the table after cursor
        const afterPos = $from.pos;
        const afterTableEndPos = afterPos + after.nodeSize - 1;
        editor.chain().focus().setTextSelection(afterTableEndPos).insertContent({
          type: 'externalLinkRow',
          attrs: {
            href: url && url.trim() !== '' && url !== 'https://' ? url.trim() : null,
          },
          content: [
            {
              type: 'paragraph',
              content: [
                {
                  type: 'text',
                  text: 'Click to edit title'
                }
              ]
            }
          ]
        }).run();
      } else {
        // Create new external link table
        editor.chain().focus().setExternalLink({
          href: url && url.trim() !== '' && url !== 'https://' ? url.trim() : null,
        }).run();
      }
    }
  };

  const insertMeeting = () => {
    const url = prompt('Enter URL (optional):');
    if (url === null) return;

    const { $from } = editor.state.selection;
    const insertCtx = resolveResourceTableRowInsert(editor.state);

    if (insertCtx) {
      editor.chain().focus().setTextSelection(insertCtx.insertPos).insertContent({
        type: 'meetingRow',
        attrs: {
          href: url && url.trim() !== '' && url !== 'https://' ? url.trim() : null,
        },
        content: [
          {
            type: 'paragraph',
            content: [
              {
                type: 'text',
                text: 'Click to edit title'
              }
            ]
          }
        ]
      }).run();
      afterResourceRowInsertFocus(editor, editor.state, {
        insertPos: insertCtx.insertPos,
        tablePos: insertCtx.tablePos,
        rowType: 'meetingRow',
      });
    } else {
      const before = $from.nodeBefore;
      const after = $from.nodeAfter;
      
      if (before && before.type.name === 'resourceTable') {
        const beforePos = $from.pos - before.nodeSize;
        const beforeTableEndPos = beforePos + before.nodeSize - 1;
        
        editor.chain().focus().setTextSelection(beforeTableEndPos).insertContent({
          type: 'meetingRow',
          attrs: {
            href: url && url.trim() !== '' && url !== 'https://' ? url.trim() : null,
          },
          content: [
            {
              type: 'paragraph',
              content: [
                {
                  type: 'text',
                  text: 'Click to edit title'
                }
              ]
            }
          ]
        }).run();
        afterResourceRowInsertFocus(editor, editor.state, {
          insertPos: beforeTableEndPos,
          tablePos: beforePos,
          rowType: 'meetingRow',
        });
      } else if (after && after.type.name === 'resourceTable') {
        const afterPos = $from.pos;
        const afterTableEndPos = afterPos + after.nodeSize - 1;
        
        editor.chain().focus().setTextSelection(afterTableEndPos).insertContent({
          type: 'meetingRow',
          attrs: {
            href: url && url.trim() !== '' && url !== 'https://' ? url.trim() : null,
          },
          content: [
            {
              type: 'paragraph',
              content: [
                {
                  type: 'text',
                  text: 'Click to edit title'
                }
              ]
            }
          ]
        }).run();
        afterResourceRowInsertFocus(editor, editor.state, {
          insertPos: afterTableEndPos,
          tablePos: afterPos,
          rowType: 'meetingRow',
        });
      } else {
        const beforePos = editor.state.selection.from;
        editor.chain().focus().setMeeting({
          href: url && url.trim() !== '' && url !== 'https://' ? url.trim() : null,
        }).run();
        afterResourceRowInsertFocus(editor, editor.state, {
          rowType: 'meetingRow',
          insertPos: beforePos,
        });
      }
    }
  };

  const isSimpleMode = toolbarMode === 'simple';
  // Blockquotes use the Pathstone quote variants for typography, so the font
  // dropdown should be locked the same way it is for heading/subheading/caption.
  const paragraphFontFamilyLocked =
    paragraphStyleLocksFontFamily(toolbarState.postTextStyle) ||
    !!toolbarState.blockquote;
  const paragraphStyleMenuLocked = isPostEditorParagraphStyleMenuLocked(toolbarState);
  const listControlsLocked =
    toolbarState.blockquote ||
    isPostEditorListLockedForParagraphStyle(toolbarState.postTextStyle);
  const listControlsLockTooltip = toolbarState.blockquote
    ? POST_EDITOR_BLOCKQUOTE_LIST_LOCK_TOOLTIP
    : POST_EDITOR_HEADING_LIST_LOCK_TOOLTIP;
  const linkLocked = isPostEditorInlineLinkLocked(toolbarState);
  const linkLockedForNewLinks = linkLocked && !editor?.isActive('link');
  const linkLockTooltip = postEditorInlineLinkLockTooltip(toolbarState);
  const effectiveParagraphStyle = effectivePostParagraphStyle(toolbarState.postTextStyle);
  const colorPickerCtx = resolvePostEditorColorPickerContext(toolbarState, { featuresPreset });
  const compactTextColorMenu =
    colorPickerCtx.mode === POST_EDITOR_COLOR_PICKER_MODES.announcement ||
    colorPickerCtx.mode === POST_EDITOR_COLOR_PICKER_MODES.full;
  const compactTextColorOptions =
    colorPickerCtx.mode === POST_EDITOR_COLOR_PICKER_MODES.announcement
      ? POST_EDITOR_ANNOUNCEMENT_TEXT_COLORS
      : POST_EDITOR_INLINE_TEXT_COLORS;
  const textColorDisabled = colorPickerCtx.mode === POST_EDITOR_COLOR_PICKER_MODES.disabled;
  const textColorDisabledTooltip =
    toolbarState.resourceTable
      ? 'Text color is disabled in resource rows'
      : toolbarState.blockquote
        ? 'Text color is disabled in pull quotes'
        : toolbarState.orderedList
          ? 'Text color is disabled in ordered lists'
      : effectiveParagraphStyle === 'body'
        ? 'Text color is disabled for Body text'
        : toolbarState.bulletList
          ? 'Text color is disabled in unordered lists'
          : 'Text color is disabled';

  useEffect(() => {
    if (textColorDisabled) setShowColorDropdown(false);
  }, [textColorDisabled]);

  useEffect(() => {
    if (toolbarState.blockquote) setHrPickerOpen(false);
  }, [toolbarState.blockquote]);

  const applyPostParagraphStyle = (style, accent) => {
    if (!editor) return;
    const resolvedAccent =
      accent ??
      (style === 'heading' || style === 'subheading'
        ? toolbarState.postTextAccent || 'black'
        : undefined);
    editor.chain().focus().setPostParagraphStyle({ style, accent: resolvedAccent }).run();
  };

  const handleParagraphStyleChange = (event) => {
    applyPostParagraphStyle(event.target.value);
  };

  return (
    <div className={`tiptap-toolbar ${className}`}>
      {!isSimpleMode && (enableMediaUI || (enableResourceUI && enableFileUI)) && (
        <>
          {enableMediaUI && (
            <input 
              type="file" 
              accept="image/*,image/svg+xml,video/*" 
              hidden
              ref={fileInputRef}
              onChange={handleFileSelect}
            />
          )}
          {(enableResourceUI && enableFileUI) && (
            <input
              type="file"
              accept="*/*"
              hidden
              ref={downloadInputRef}
              onChange={handleDownloadSelect}
            />
          )}
        </>
      )}
      
      <div className="toolbar-group toolbar-group--text-style">
        {!isSimpleMode && (
          <select
            value={effectiveParagraphStyle}
            onChange={handleParagraphStyleChange}
            title={postEditorParagraphStyleLockTooltip(toolbarState) || 'Paragraph style'}
            disabled={paragraphStyleMenuLocked}
            className="ps-toolbar-paragraph-style-select"
          >
            {POST_EDITOR_PARAGRAPH_STYLE_OPTIONS.map((style) => (
              <option key={style.value} value={style.value}>
                {style.label}
              </option>
            ))}
          </select>
        )}

        <div className="ps-toolbar-popover-anchor" ref={colorDropdownRef}>
          <button
            type="button"
            onClick={() => {
              if (textColorDisabled) return;
              setShowColorDropdown(!showColorDropdown);
            }}
            title={
              textColorDisabled ? textColorDisabledTooltip : colorPickerCtx.title
            }
            className={[
              'ps-color-trigger',
              showColorDropdown ? 'active' : '',
              compactTextColorMenu &&
              normalizePsTextColorToken(toolbarState.color) === 'ps-red'
                ? 'ps-color-trigger--announcement-red'
                : '',
            ]
              .filter(Boolean)
              .join(' ')}
            disabled={textColorDisabled}
          >
            <TextColorSmallIcon className={colorTriggerIconClass(toolbarState, colorPickerCtx)} />
            <span className="ps-color-trigger__chevron">▼</span>
          </button>

          {showColorDropdown && (
            <div
              className={[
                'ps-color-menu',
                compactTextColorMenu
                  ? 'ps-announcement-color-menu'
                  : 'ps-color-menu--limited',
              ].join(' ')}
            >
              {!compactTextColorMenu && (
                <div className="ps-toolbar-menu__title">{colorPickerCtx.title}</div>
              )}

              {compactTextColorMenu && (
                <TextColorListMenu
                  colors={compactTextColorOptions}
                  selectedToken={toolbarState.color}
                  onSelect={(id) => {
                    applyToolbarColor(id);
                    setShowColorDropdown(false);
                  }}
                />
              )}

              {(colorPickerCtx.mode === POST_EDITOR_COLOR_PICKER_MODES.heading ||
                colorPickerCtx.mode === POST_EDITOR_COLOR_PICKER_MODES.subheading) && (
                <div className="ps-accent-grid">
                  {POST_EDITOR_HEADING_ACCENTS.map((accent) => {
                    const selected = (toolbarState.postTextAccent || 'black') === accent.id;
                    const letter =
                      colorPickerCtx.mode === POST_EDITOR_COLOR_PICKER_MODES.heading ? 'H' : 'S';
                    return (
                      <button
                        key={accent.id}
                        type="button"
                        title={accent.label}
                        aria-label={`${colorPickerCtx.title} ${accent.label}`}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => {
                          applyToolbarColor(accent.id);
                          setShowColorDropdown(false);
                        }}
                        className={['ps-accent-tile', selected ? 'is-selected' : ''].filter(Boolean).join(' ')}
                      >
                        <span className={`ps-accent-tile__letter ps-heading-${accent.id}`}>{letter}</span>
                        <span className="ps-accent-tile__label">{accent.label}</span>
                      </button>
                    );
                  })}
                </div>
              )}

              {colorPickerCtx.mode === POST_EDITOR_COLOR_PICKER_MODES.quote && (
                <QuoteColorPickerMenu
                  selectedVariant={toolbarState.quoteVariant || 'black'}
                  onSelect={(id) => {
                    applyToolbarColor(id);
                    setShowColorDropdown(false);
                  }}
                />
              )}

              {colorPickerCtx.mode === POST_EDITOR_COLOR_PICKER_MODES.orderedList && (
                <div className="ps-marker-grid">
                  {POST_EDITOR_ORDERED_MARKER_COLORS.map((c) => {
                    const selected = toolbarState.orderedListMarker === c;
                    const sw = toolbarSwatchClasses(c);
                    const label = orderedMarkerColorLabel(c);
                    return (
                      <button
                        key={c}
                        type="button"
                        title={`Marker ${label}`}
                        aria-label={`Use marker color ${label}`}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => {
                          applyToolbarColor(c);
                          setShowColorDropdown(false);
                        }}
                        className={['ps-ol-swatch', selected ? 'is-selected' : ''].join(' ')}
                      >
                        <span className={['ps-ol-swatch__marker', sw.text].filter(Boolean).join(' ')}>01.</span>
                        <span className="ps-ol-swatch__lines" aria-hidden="true">
                          <span className={['ps-ol-swatch__line', 'ps-ol-swatch__line--opacity-40', sw.bg].filter(Boolean).join(' ')} />
                          <span className={['ps-ol-swatch__line', 'ps-ol-swatch__line--opacity-28', 'ps-ol-swatch__line--w-72', sw.bg].filter(Boolean).join(' ')} />
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}

            </div>
          )}
        </div>

        <select
          value={toolbarState.fontFamilyClass || 'font-primary'}
          onChange={handleFontFamilyChange}
          title={
            paragraphFontFamilyLocked
              ? 'Font family is locked for Heading/Subheading/Caption/Quotes'
              : 'Font family'
          }
          disabled={paragraphFontFamilyLocked}
        >
          {POST_EDITOR_FONT_FAMILIES.map((font) => (
            <option key={font.value} value={font.value}>
              {font.label}
            </option>
          ))}
        </select>
      </div>

      <span className="toolbar-separator" />

      <div className="toolbar-group">
        <button 
          type="button" 
          onClick={() => editor.chain().focus().toggleUnderline().run()} 
          className={toolbarState.underline ? 'active' : ''} 
          title="Underline"
        >
          <svg width="24" height="24" className="tiptap-button-icon" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" clipRule="evenodd" d="M7 4C7 3.44772 6.55228 3 6 3C5.44772 3 5 3.44772 5 4V10C5 11.8565 5.7375 13.637 7.05025 14.9497C8.36301 16.2625 10.1435 17 12 17C13.8565 17 15.637 16.2625 16.9497 14.9497C18.2625 13.637 19 11.8565 19 10V4C19 3.44772 18.5523 3 18 3C17.4477 3 17 3.44772 17 4V10C17 11.3261 16.4732 12.5979 15.5355 13.5355C14.5979 14.4732 13.3261 15 12 15C10.6739 15 9.40215 14.4732 8.46447 13.5355C7.52678 12.5979 7 11.3261 7 10V4ZM4 19C3.44772 19 3 19.4477 3 20C3 20.5523 3.44772 21 4 21H20C20.5523 21 21 20.5523 21 20C21 19.4477 20.5523 19 20 19H4Z" fill="currentColor"></path></svg>
        </button>
        {enableSuperscript && (
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleSuperscript().run()}
            className={toolbarState.superscript ? 'active' : ''}
            title="Superscript"
          >
            <svg width="24" height="24" className="tiptap-button-icon" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" clipRule="evenodd" d="M12.7071 7.29289C13.0976 7.68342 13.0976 8.31658 12.7071 8.70711L4.70711 16.7071C4.31658 17.0976 3.68342 17.0976 3.29289 16.7071C2.90237 16.3166 2.90237 15.6834 3.29289 15.2929L11.2929 7.29289C11.6834 6.90237 12.3166 6.90237 12.7071 7.29289Z" fill="currentColor"></path><path fillRule="evenodd" clipRule="evenodd" d="M3.29289 7.29289C3.68342 6.90237 4.31658 6.90237 4.70711 7.29289L12.7071 15.2929C13.0976 15.6834 13.0976 16.3166 12.7071 16.7071C12.3166 17.0976 11.6834 17.0976 11.2929 16.7071L3.29289 8.70711C2.90237 8.31658 2.90237 7.68342 3.29289 7.29289Z" fill="currentColor"></path><path fillRule="evenodd" clipRule="evenodd" d="M17.405 1.40657C18.0246 1.05456 18.7463 0.92634 19.4492 1.04344C20.1521 1.16054 20.7933 1.51583 21.2652 2.0497L21.2697 2.05469L21.2696 2.05471C21.7431 2.5975 22 3.28922 22 4.00203C22 5.08579 21.3952 5.84326 20.7727 6.34289C20.1966 6.80531 19.4941 7.13675 18.9941 7.37261C18.9714 7.38332 18.9491 7.39383 18.9273 7.40415C18.4487 7.63034 18.2814 7.78152 18.1927 7.91844C18.1778 7.94155 18.1625 7.96834 18.1473 8.00003H21C21.5523 8.00003 22 8.44774 22 9.00003C22 9.55231 21.5523 10 21 10H17C16.4477 10 16 9.55231 16 9.00003C16 8.17007 16.1183 7.44255 16.5138 6.83161C16.9107 6.21854 17.4934 5.86971 18.0728 5.59591C18.6281 5.33347 19.1376 5.09075 19.5208 4.78316C19.8838 4.49179 20 4.25026 20 4.00203C20 3.77192 19.9178 3.54865 19.7646 3.37182C19.5968 3.18324 19.3696 3.05774 19.1205 3.01625C18.8705 2.97459 18.6137 3.02017 18.3933 3.14533C18.1762 3.26898 18.0191 3.45826 17.9406 3.67557C17.7531 4.19504 17.18 4.46414 16.6605 4.27662C16.141 4.0891 15.8719 3.51596 16.0594 2.99649C16.303 2.3219 16.7817 1.76125 17.4045 1.40689L17.405 1.40657Z" fill="currentColor"></path></svg>
          </button>
        )}
      </div>

      <span className="toolbar-separator" />

      {!isSimpleMode && (
        <>
          <div className="toolbar-group">
            <button
              type="button"
              onClick={() => {
                editor.chain().focus().toggleBulletList().run();
              }}
              className={toolbarState.bulletList ? 'active' : ''}
              title={listControlsLocked ? listControlsLockTooltip : 'Bulleted list'}
              disabled={listControlsLocked}
            >
              <ListIcon />
            </button>

            <button
              type="button"
              onClick={() => {
                editor.chain().focus().toggleOrderedList().run();
              }}
              className={toolbarState.orderedList ? 'active' : ''}
              title={listControlsLocked ? listControlsLockTooltip : 'Numbered list'}
              disabled={listControlsLocked}
            >
              <ListOrderedIcon />
            </button>

            <button
              type="button"
              onClick={() => {
                if (toolbarState.blockquote) {
                  editor.chain().focus().unsetBlockquote().run();
                } else {
                  editor.chain().focus().insertPathstoneQuote('black').run();
                }
              }}
              className={toolbarState.blockquote ? 'active' : ''}
              title="Pull quote. Use text color for quote tone; add attribution from the quote menu."
            >
              <BlockquoteIcon />
            </button>
          </div>

          <span className="toolbar-separator" />
        </>
      )}


      <div className="toolbar-group">
        <button 
          type="button" 
          onClick={toggleLink} 
          className={toolbarState.link ? 'active' : ''} 
          title={linkLockedForNewLinks ? linkLockTooltip : linkLocked ? 'Remove link (clear URL)' : 'Link'}
          disabled={linkLockedForNewLinks}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
            <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
          </svg>
        </button>


        {enableMediaUI && (
          <button 
            type="button" 
            onClick={() => {
              setResourceModalType('image');
              setResourceModalOpen(true);
            }} 
            title="Insert Image/Video"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="currentColor"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M20 2C20 1.44772 19.5523 1 19 1C18.4477 1 18 1.44772 18 2V4H16C15.4477 4 15 4.44772 15 5C15 5.55228 15.4477 6 16 6H18V8C18 8.55228 18.4477 9 19 9C19.5523 9 20 8.55228 20 8V6H22C22.5523 6 23 5.55228 23 5C23 4.44772 22.5523 4 22 4H20V2ZM5 4C4.73478 4 4.48043 4.10536 4.29289 4.29289C4.10536 4.48043 4 4.73478 4 5V19C4 19.2652 4.10536 19.5196 4.29289 19.7071C4.48043 19.8946 4.73478 20 5 20H5.58579L14.379 11.2068C14.9416 10.6444 15.7045 10.3284 16.5 10.3284C17.2955 10.3284 18.0584 10.6444 18.621 11.2068L20 12.5858V12C20 11.4477 20.4477 11 21 11C21.5523 11 22 11.4477 22 12V14.998C22 14.9994 22 15.0007 22 15.002V19C22 19.7957 21.6839 20.5587 21.1213 21.1213C20.5587 21.6839 19.7957 22 19 22H6.00219C6.00073 22 5.99927 22 5.99781 22H5C4.20435 22 3.44129 21.6839 2.87868 21.1213C2.31607 20.5587 2 19.7957 2 19V5C2 4.20435 2.31607 3.44129 2.87868 2.87868C3.44129 2.31607 4.20435 2 5 2H12C12.5523 2 13 2.44772 13 3C13 3.55228 12.5523 4 12 4H5ZM8.41422 20H19C19.2652 20 19.5196 19.8946 19.7071 19.7071C19.8946 19.5196 20 19.2652 20 19V15.4142L17.207 12.6212C17.0195 12.4338 16.7651 12.3284 16.5 12.3284C16.2349 12.3284 15.9806 12.4337 15.7931 12.6211L8.41422 20ZM6.87868 6.87868C7.44129 6.31607 8.20435 6 9 6C9.79565 6 10.5587 6.31607 11.1213 6.87868C11.6839 7.44129 12 8.20435 12 9C12 9.79565 11.6839 10.5587 11.1213 11.1213C10.5587 11.6839 9.79565 12 9 12C8.20435 12 7.44129 11.6839 6.87868 11.1213C6.31607 10.5587 6 9.79565 6 9C6 8.20435 6.31607 7.44129 6.87868 6.87868ZM9 8C8.73478 8 8.48043 8.10536 8.29289 8.29289C8.10536 8.48043 8 8.73478 8 9C8 9.26522 8.10536 9.51957 8.29289 9.70711C8.48043 9.89464 8.73478 10 9 10C9.26522 10 9.51957 9.89464 9.70711 9.70711C9.89464 9.51957 10 9.26522 10 9C10 8.73478 9.89464 8.48043 9.70711 8.29289C9.51957 8.10536 9.26522 8 9 8Z"
                fill="currentColor"
              />
            </svg>
          </button>
        )}

        {!isSimpleMode && (
          <>
            <span ref={hrPickerRef} className="ps-hr-picker-anchor">
              <button
                type="button"
                onClick={() => {
                  if (toolbarState.blockquote) return;
                  setHrPickerOpen((o) => !o);
                }}
                title={
                  toolbarState.blockquote
                    ? POST_EDITOR_BLOCKQUOTE_HR_LOCK_TOOLTIP
                    : 'Horizontal rule (preview: top row 1px, bottom row 5px)'
                }
                disabled={toolbarState.blockquote}
              >
                ―
              </button>
              {hrPickerOpen && (
                <div
                  className="ps-toolbar-menu ps-toolbar-menu--w-panel"
                >
                  <div className="ps-toolbar-menu__title">Horizontal rule</div>
                  <div className="ps-toolbar-menu__subtitle">
                    Top row: thin (1px) · Bottom row: thick (5px) — tap to insert
                  </div>
                  <div className="ps-hr-grid">
                    {POST_EDITOR_HR_WIDTHS.flatMap((w) =>
                      POST_EDITOR_HR_COLORS.map((c) => {
                        const label = postEditorHrColorLabel(c);
                        return (
                        <button
                          key={`${w}-${c}`}
                          type="button"
                          title={`${w}px · ${label}`}
                          aria-label={`Insert ${w}px horizontal rule, color ${label}`}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => {
                            if (toolbarState.blockquote) return;
                            editor
                              .chain()
                              .focus()
                              .setHorizontalRule({ color: c, lineWidth: w })
                              .run();
                            setHrPickerOpen(false);
                          }}
                          disabled={toolbarState.blockquote}
                          className="ps-hr-tile"
                        >
                          <hr className={postEditorHrClassList(c, w)} />
                        </button>
                        );
                      })
                    )}
                  </div>
                </div>
              )}
            </span>

            <span ref={spacerPickerRef} className="ps-spacer-picker-anchor">
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => setSpacerPickerOpen((o) => !o)}
                className={toolbarState.spacer || spacerPickerOpen ? 'active' : ''}
                title={
                  toolbarState.spacer
                    ? `Spacer (${(POST_EDITOR_SPACER_SIZE_LABELS[toolbarState.spacerSize] || '?')} · ${POST_EDITOR_SPACER_SIZES[toolbarState.spacerSize] || ''}px) — pick to resize`
                    : 'Insert vertical spacer'
                }
                aria-label="Vertical spacer"
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <line x1="4" y1="5" x2="20" y2="5" />
                  <path d="M12 9 v6" />
                  <path d="M9 11 l3 -2 l3 2" />
                  <path d="M9 13 l3 2 l3 -2" />
                  <line x1="4" y1="19" x2="20" y2="19" />
                </svg>
              </button>
              {spacerPickerOpen && (
                <div className="ps-toolbar-menu ps-toolbar-menu--w-panel">
                  <div className="ps-toolbar-menu__title">
                    {toolbarState.spacer ? 'Resize spacer' : 'Insert spacer'}
                  </div>
                  <div className="ps-toolbar-menu__subtitle">
                    Invisible vertical spacer for extra breathing room.
                  </div>
                  <div className="ps-spacer-grid">
                    {POST_EDITOR_SPACER_SIZE_KEYS.map((k) => {
                      const label = POST_EDITOR_SPACER_SIZE_LABELS[k];
                      const px = POST_EDITOR_SPACER_SIZES[k];
                      const isCurrent = toolbarState.spacer && toolbarState.spacerSize === k;
                      return (
                        <button
                          key={k}
                          type="button"
                          className={`ps-spacer-tile${isCurrent ? ' ps-spacer-tile--active' : ''}`}
                          title={`${label} — ${px}px`}
                          aria-label={`${toolbarState.spacer ? 'Resize spacer to' : 'Insert spacer'} ${label} (${px}px)`}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => {
                            if (toolbarState.spacer) {
                              editor.chain().focus().updatePostSpacerSize(k).run();
                            } else {
                              editor.chain().focus().setPostSpacer({ size: k }).run();
                            }
                            setSpacerPickerOpen(false);
                          }}
                        >
                          <span className="ps-spacer-tile__label">{label}</span>
                          <span className="ps-spacer-tile__px">{px}px</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </span>

            <span className="toolbar-separator" />

            {enableResourceUI && (
              <>
                {enableFileUI && (
                  <>
                    <button 
                      type="button" 
                      onClick={() => {
                        setResourceModalType('download');
                        setResourceModalOpen(true);
                      }} 
                      title="Download Section"
                    >
                      <span className="ps-toolbar-resource-icon" aria-hidden="true">
                        <i className="icon-ps-download" />
                      </span>
                    </button>
                    <button 
                      type="button" 
                      onClick={() => {
                        setResourceModalType('audio');
                        setResourceModalOpen(true);
                      }} 
                      title="Audio Section"
                    >
                      <span className="ps-toolbar-resource-icon" aria-hidden="true">
                        <i className="icon-ps-audio" />
                      </span>
                    </button>
                  </>
                )}
            
                <button 
                  type="button" 
                  onClick={insertExternalLink} 
                  title="External Link Section"
                >
                  <span className="ps-toolbar-resource-icon" aria-hidden="true">
                    <i className="icon-ps-link" />
                  </span>
                </button>
                <button 
                  type="button" 
                  onClick={insertMeeting} 
                  title="Meeting Section"
                >
                  <span className="ps-toolbar-resource-icon" aria-hidden="true">
                    <i className="icon-ps-video" />
                  </span>
                </button>
              </>
            )}
          </>
        )}
      </div>
    
      {!isSimpleMode && (
        <>
          <span className="toolbar-separator" />

          <div className="toolbar-group">
            <button
              type="button"
              onClick={() => editor.chain().focus().indentBlock().run()}
              title="Increase indent (Tab)"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path d="M4 5h16M4 12h10M4 19h16" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" />
                <path d="M16 9l4 3-4 3" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <button
              type="button"
              onClick={() => editor.chain().focus().outdentBlock().run()}
              title="Decrease indent (Shift+Tab)"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path d="M4 5h16M10 12h10M4 19h16" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" />
                <path d="M8 9l-4 3 4 3" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>
        </>
      )}
      {(isLoading || isUploadingDownload) && (
        <div className="tiptap-upload-loader">
          <div className="tiptap-loader-spinner" />
        </div>
      )}
      {(enableResourceUI || enableMediaUI || (enableResourceUI && enableFileUI)) && (
        <ResourceModal
          isOpen={resourceModalOpen}
          fileType={resourceModalType}
          onClose={() => {
            setResourceModalOpen(false);
            setResourceModalType(null);
            setInitialLinkUrl(''); // Reset initial URL when modal closes
            setInitialFiles(null); // Reset initial files when modal closes
            // Reset replacement tracking when modal closes
            currentReplaceRowPos.current = null;
            currentReplaceRowType.current = null;
            // Reset carousel-edit / per-slide replace tracking
            currentCarouselEditPos.current = null;
            setCurrentCarouselEditSlides(null);
            currentCarouselReplaceSlideIdx.current = null;
            currentCarouselReplaceSlide.current = null;
            currentCarouselReplaceLockedSlides.current = [];
          }}
          initialFiles={initialFiles}
          onSelect={handleResourceSelect}
          onCarouselInsert={handleCarouselInsert}
          onCarouselUpdate={handleCarouselUpdate}
          existingCarouselSlides={currentCarouselEditSlides}
          replaceCarouselSlide={currentCarouselReplaceSlide.current}
          lockedCarouselSlides={currentCarouselReplaceLockedSlides.current}
          editor={editor}
          initialLinkUrl={initialLinkUrl}
        />
      )}
    </div>
  );
};
